import { useState, useEffect, useCallback, useRef } from "react";
import * as XLSX from "xlsx";

const API    = process.env.REACT_APP_API_URL || "http://localhost:4000/api";
const getToken = () => localStorage.getItem("sellsync_token");

const api = async (path, opts = {}) => {
  const res = await fetch(API + path, {
    ...opts,
    headers: { "Content-Type":"application/json", ...(getToken()?{Authorization:"Bearer "+getToken()}:{}), ...opts.headers },
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Request failed");
  return data;
};

const INR = (n) => "₹" + Number(n||0).toLocaleString("en-IN");

const CITIES = ["Ghaziabad", "Delhi", "Mumbai", "Bangalore", "Hyderabad", "Chennai", "Kolkata", "Pune", "Ahmedabad", "Surat"];
const PLATFORMS = ["shopify","amazon","flipkart","myntra","manual"];

const STATUS_COLORS = {
  // warehouse transfer
  draft:       { color:"#6b7280", bg:"#f3f4f6" },
  in_transit:  { color:"#d97706", bg:"#fffbeb" },
  received:    { color:"#15803d", bg:"#f0fdf4" },
  cancelled:   { color:"#dc2626", bg:"#fef2f2" },
  // picklist
  pending:     { color:"#6b7280", bg:"#f3f4f6" },
  picking:     { color:"#2563eb", bg:"#eff6ff" },
  picked:      { color:"#7c3aed", bg:"#f5f3ff" },
  packing:     { color:"#d97706", bg:"#fffbeb" },
  packed:      { color:"#0891b2", bg:"#ecfeff" },
  dispatched:  { color:"#15803d", bg:"#f0fdf4" },
  // forecast
  out_of_stock:{ color:"#dc2626", bg:"#fef2f2" },
  critical:    { color:"#dc2626", bg:"#fef2f2" },
  low:         { color:"#d97706", bg:"#fffbeb" },
  healthy:     { color:"#15803d", bg:"#f0fdf4" },
};

const S = {
  card:    { background:"#fff", borderRadius:10, border:"1px solid #e5e7eb", marginBottom:14 },
  cardHdr: { padding:"11px 16px", borderBottom:"1px solid #e5e7eb", display:"flex", alignItems:"center", justifyContent:"space-between", fontWeight:600, fontSize:13 },
  th:      { padding:"8px 12px", textAlign:"left", fontSize:11, fontWeight:600, color:"#6b7280", background:"#f9fafb", whiteSpace:"nowrap" },
  td:      { padding:"9px 12px", borderBottom:"1px solid #f3f4f6", verticalAlign:"middle", fontSize:13 },
  btn:     (c="#F47920") => ({ padding:"7px 14px", borderRadius:7, fontSize:12, cursor:"pointer", background:c, color:"#fff", border:"none", fontFamily:"inherit", fontWeight:600 }),
  btnOut:  { padding:"6px 12px", borderRadius:7, fontSize:11, cursor:"pointer", background:"#fff", color:"#374151", border:"1px solid #d1d5db", fontFamily:"inherit" },
  input:   { width:"100%", padding:"7px 10px", borderRadius:7, border:"1px solid #d1d5db", fontSize:13, fontFamily:"inherit", outline:"none", marginBottom:10 },
  label:   { fontSize:11, color:"#6b7280", fontWeight:600, display:"block", marginBottom:3 },
  overlay: { position:"fixed", inset:0, background:"rgba(0,0,0,0.4)", zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center" },
  modal:   { background:"#fff", borderRadius:12, padding:24, width:560, maxWidth:"95vw", maxHeight:"90vh", overflowY:"auto", boxShadow:"0 20px 60px rgba(0,0,0,0.2)" },
  tab:     (a) => ({ padding:"10px 20px", background:"none", border:"none", borderBottom:a?"2px solid #F47920":"2px solid transparent", color:a?"#F47920":"#6b7280", fontWeight:a?600:400, fontSize:13, cursor:"pointer", fontFamily:"inherit" }),
  kpi:     { background:"#fff", borderRadius:10, border:"1px solid #e5e7eb", padding:"14px 16px" },
};

const Badge = ({ label, color, bg }) => (
  <span style={{ background:bg, color, padding:"2px 9px", borderRadius:20, fontSize:11, fontWeight:600, whiteSpace:"nowrap" }}>{label}</span>
);

const EMPTY_WH = { name:"", code:"", address:"", city:"Ghaziabad", state:"Uttar Pradesh", pincode:"", phone:"", manager:"", shopifyLocationId:"", notes:"" };
const EMPTY_TRF = { fromWarehouse:"", toWarehouse:"", reason:"", notes:"", items:[{ productName:"", shopifySku:"", requestedQty:1, unit:"pcs" }] };
const EMPTY_PCK = { warehouseId:"", orderNumber:"", platform:"shopify", customerName:"", assignedTo:"", notes:"", items:[{ productName:"", shopifySku:"", orderedQty:1, unit:"pcs", location:"" }] };

export default function PageWMS({ toast, products=[], orders=[] }) {
  const [tab,        setTab]        = useState("warehouses");
  const [warehouses, setWarehouses] = useState([]);
  const [transfers,  setTransfers]  = useState([]);
  const [picklists,  setPicklists]  = useState([]);
  const [forecast,   setForecast]   = useState([]);
  const [loading,    setLoading]    = useState(false);
  const [forecasting,setForecasting]= useState(false);

  // modals
  const [whModal,   setWhModal]   = useState(false);
  const [editWh,    setEditWh]    = useState(null);
  const [whForm,    setWhForm]    = useState(EMPTY_WH);
  const [trfModal,  setTrfModal]  = useState(false);
  const [trfForm,   setTrfForm]   = useState(EMPTY_TRF);
  const [pckModal,  setPckModal]  = useState(false);
  const [pckForm,   setPckForm]   = useState(EMPTY_PCK);
  const [activePck, setActivePck] = useState(null);
  const [repModal,  setRepModal]  = useState(false);
  const [repItems,  setRepItems]  = useState([]);
  const [repSupplier,setRepSupplier]= useState({ name:"", email:"" });
  const [suppliers,  setSuppliers]  = useState([]);
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [aiLoading,     setAiLoading]     = useState(false);
  const [aiDone,        setAiDone]        = useState(false);
  const [selSuggestions,setSelSuggestions]= useState([]);

  // warehouse stock
  const [stock,       setStock]      = useState([]);
  const [stockModal,  setStockModal] = useState(false);
  const [editStock,   setEditStock]  = useState(null);
  const [stockWh,     setStockWh]    = useState("");
  const [stockForm,   setStockForm]  = useState({ warehouseId:"", sku:"", productName:"", category:"", hsn:"", unit:"pcs", qty:0, minQty:0, costPrice:0, mrp:0, location:"", notes:"" });
  const [adjModal,    setAdjModal]   = useState(false);
  const [adjItem,     setAdjItem]    = useState(null);
  const [adjQty,      setAdjQty]     = useState(0);
  const [adjReason,   setAdjReason]  = useState("");
  const [stockSearch, setStockSearch]= useState("");
  const [reportData,  setReportData] = useState(null);
  const [reportLoading,setReportLoading]= useState(false);
  const [excelUploading, setExcelUploading] = useState(false);
  const [excelPreview,   setExcelPreview]   = useState(null);  // {rows, warehouseId, errors}
  const [excelMode,      setExcelMode]      = useState("replace"); // replace | add | subtract
  const [stockSort,      setStockSort]      = useState({ field:"productName", dir:"asc" });
  const [stockScanMode,  setStockScanMode]  = useState(false);
  const [stockScanInput, setStockScanInput] = useState("");
  const [stockScanResult,setStockScanResult]= useState(null);
  const [stockScanQty,   setStockScanQty]   = useState(0);
  const [stockScanAdj,   setStockScanAdj]   = useState("set"); // set | add | subtract
  const stockFileRef = useRef(null);
  const stockScanRef = useRef(null);

  // barcode scanner
  const [scanMode,   setScanMode]   = useState(false);
  const [scanInput,  setScanInput]  = useState("");
  const [scanResult, setScanResult] = useState(null);
  const scanRef = useRef(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [wh, trf, pck, stk, sup] = await Promise.all([
        api("/wms/warehouses"),
        api("/wms/transfers"),
        api("/wms/picklists"),
        api("/wms/stock"),
        api("/suppliers").catch(() => ({ suppliers: [] })),
      ]);
      setWarehouses(wh.warehouses || []);
      setStock(stk.stock || []);
      setSuppliers(sup.suppliers || []);
      setTransfers(trf.transfers  || []);
      setPicklists(pck.picklists  || []);
    } catch(e) { toast(e.message, "error"); }
    finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // ── forecasting — all 5 platforms ───────────────────────────
  const [forecastSources, setForecastSources] = useState({});

  const runForecast = async () => {
    setForecasting(true);
    const sources = { shopify: 0, amazon: 0, flipkart: 0, myntra: 0, meesho: 0 };
    try {
      // Start with Shopify orders already in props
      let allOrders = [...orders];
      sources.shopify = orders.length;

      // Normalize orders from each platform to a common format
      // so the backend velocity calc works on line_items consistently
      const normalize = (platformOrders, platform) => {
        return platformOrders.map((o) => {
          // Already normalized Shopify format has line_items
          if (platform === "shopify") return o;

          // Amazon
          if (platform === "amazon") {
            return {
              created_at: o.PurchaseDate || o.purchaseDate || new Date().toISOString(),
              line_items: (o.items || o.OrderItems || []).map((i) => ({
                sku: i.SellerSKU || i.sellerSku || "",
                name: i.Title || i.title || "",
                quantity: parseInt(i.QuantityOrdered || i.quantity || 1),
              })),
            };
          }
          // Flipkart
          if (platform === "flipkart") {
            return {
              created_at: o.orderDate || new Date().toISOString(),
              line_items: (o.items || o.orderItems || []).map((i) => ({
                sku: i.skuId || i.sku || "",
                name: i.title || i.name || "",
                quantity: i.quantity || 1,
              })),
            };
          }
          // Myntra
          if (platform === "myntra") {
            return {
              created_at: o.orderDate || new Date().toISOString(),
              line_items: (o.items || o.subOrders || []).map((i) => ({
                sku: i.sku || "",
                name: i.title || i.name || "",
                quantity: i.quantity || 1,
              })),
            };
          }
          // Meesho
          if (platform === "meesho") {
            return {
              created_at: o.order_date || new Date().toISOString(),
              line_items: [{
                sku: o.sku || "",
                name: o.name || "",
                quantity: o.quantity || 1,
              }],
            };
          }
          return o;
        });
      };

      // Fetch all platform orders in parallel — ignore failures
      const [amzData, fkData, mynData, mshData] = await Promise.allSettled([
        api("/amazon/orders"),
        api("/flipkart/orders"),
        api("/myntra/orders"),
        api("/meesho/orders"),
      ]);

      if (amzData.status === "fulfilled") {
        const amzOrders = amzData.value?.payload?.Orders || amzData.value?.orders || [];
        const norm = normalize(amzOrders, "amazon");
        allOrders = [...allOrders, ...norm];
        sources.amazon = norm.length;
      }
      if (fkData.status === "fulfilled") {
        const fkOrders = fkData.value?.orders || [];
        const norm = normalize(fkOrders, "flipkart");
        allOrders = [...allOrders, ...norm];
        sources.flipkart = norm.length;
      }
      if (mynData.status === "fulfilled") {
        const mynOrders = mynData.value?.orders || [];
        const norm = normalize(mynOrders, "myntra");
        allOrders = [...allOrders, ...norm];
        sources.myntra = norm.length;
      }
      if (mshData.status === "fulfilled") {
        const mshOrders = mshData.value?.orders || [];
        const norm = normalize(mshOrders, "meesho");
        allOrders = [...allOrders, ...norm];
        sources.meesho = norm.length;
      }

      setForecastSources(sources);

      const data = await api("/wms/forecast", {
        method: "POST",
        body: JSON.stringify({ products, orders: allOrders }),
      });
      setForecast(data.forecast || []);
      const total = Object.values(sources).reduce((a,b)=>a+b,0);
      toast(`Forecast updated ✓ — ${total} orders across ${Object.values(sources).filter(v=>v>0).length} platforms`, "success");
    } catch(e) { toast(e.message, "error"); }
    finally { setForecasting(false); }
  };

  useEffect(() => { if (tab === "forecast" && products.length > 0) runForecast(); }, [tab]);

  // ── AI Reorder Suggestions ────────────────────────────────────
  const generateAiSuggestions = async () => {
    if (!forecast.length) { toast("Run forecast first", "error"); return; }
    setAiLoading(true); setAiDone(false);
    try {
      const needsAction = forecast.filter((f) => f.needsReorder || f.status === "out_of_stock" || f.status === "critical" || f.status === "low");
      if (!needsAction.length) { toast("All products are healthy — no reorder needed!", "success"); setAiLoading(false); return; }

      const response = await fetch(API + "/ai/anthropic-proxy", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: "Bearer " + getToken() },
        body: JSON.stringify({
          model: "claude-sonnet-4-20250514",
          max_tokens: 2000,
          messages: [{
            role: "user",
            content: `You are an inventory manager AI for a leather belts brand (Vasudeva Enterprises / Leather Patta) in India.
Analyze these products that need reordering and give smart suggestions.

Products needing reorder:
${JSON.stringify(needsAction.map((f) => ({
  name: f.name, sku: f.sku, currentStock: f.totalStock,
  dailySales: f.dailyVelocity, daysLeft: f.daysLeft,
  reorderPoint: f.reorderPoint, suggestedQty: f.suggestedOrder,
  status: f.status
})), null, 2)}

For each product, provide a reorder suggestion. Return ONLY a JSON array, no markdown. Each object:
{
  "sku": "string",
  "name": "string",
  "urgency": "critical|high|medium|low",
  "recommendedQty": number,
  "reasoning": "1 sentence why this qty and timing",
  "orderWithin": "string like 'Today', 'Within 2 days', 'This week'",
  "estimatedStockoutDate": "string like 'May 18' or 'Already out'",
  "unitPrice": 0
}`
          }]
        })
      });
      const data = await response.json();
      const text = data.content?.[0]?.text || "[]";
      const clean = text.replace(/\`\`\`json|\`\`\`/g, "").trim();
      const parsed = JSON.parse(clean);
      // Merge AI suggestions with forecast data
      const merged = parsed.map((s) => {
        const fc = forecast.find((f) => f.sku === s.sku) || {};
        return { ...fc, ...s, selected: true, supplierName: suppliers[0]?.name || "", supplierEmail: suppliers[0]?.email || "" };
      });
      setAiSuggestions(merged);
      setSelSuggestions(merged.map((_,i) => i));
      setAiDone(true);
      toast(`🤖 AI generated ${merged.length} reorder suggestions`, "success");
    } catch(e) { toast("AI suggestion failed: "+e.message, "error"); }
    setAiLoading(false);
  };

  const createAiPOs = async () => {
    const selected = aiSuggestions.filter((_,i) => selSuggestions.includes(i));
    if (!selected.length) { toast("Select at least one item", "error"); return; }
    // Group by supplier
    const bySupplier = {};
    selected.forEach((s) => {
      const key = s.supplierName || "Unknown Supplier";
      if (!bySupplier[key]) bySupplier[key] = { name:key, email:s.supplierEmail||"", items:[] };
      bySupplier[key].items.push(s);
    });
    let created = 0;
    for (const sup of Object.values(bySupplier)) {
      try {
        await api("/wms/replenish", { method:"POST", body:JSON.stringify({
          items: sup.items.map((s) => ({ name:s.name, sku:s.sku, suggestedOrder:s.recommendedQty||s.suggestedOrder||1, unitPrice:s.unitPrice||0 })),
          supplierName:  sup.name,
          supplierEmail: sup.email,
        })});
        created++;
      } catch(e) { toast("PO creation failed for "+sup.name+": "+e.message, "error"); }
    }
    toast(`✅ Created ${created} Purchase Order(s) — check Purchase Orders page`, "success");
    setRepModal(false); setAiDone(false); setAiSuggestions([]);
  };

  // ── parse excel for stock upload ─────────────────────────────
  const handleStockExcel = async (file, warehouseId) => {
    if (!file) return;
    if (!warehouseId) { toast("Select a warehouse first", "error"); return; }
    setExcelUploading(true);
    try {
      const buf  = await file.arrayBuffer();
      const wb   = XLSX.read(buf);
      const ws   = wb.Sheets[wb.SheetNames[0]];
      const raw  = XLSX.utils.sheet_to_json(ws, { header:1, defval:"" });
      if (!raw.length) { toast("File is empty", "error"); setExcelUploading(false); return; }
      const headers = raw[0].map((h) => String(h).toLowerCase().trim());
      const fi = (kws) => headers.findIndex((h) => kws.some((k) => h.includes(k)));
      const skuIdx  = fi(["sku","item code","product code","barcode"]);
      const nameIdx = fi(["product name","name","item name","description","title"]);
      const qtyIdx  = fi(["qty","quantity","stock","units","closing stock"]);
      const catIdx  = fi(["category","type","product type"]);
      const hsnIdx  = fi(["hsn","hsn code"]);
      const unitIdx = fi(["unit","uom","unit of measure"]);
      const minIdx  = fi(["min qty","minimum","reorder","min stock","alert"]);
      const mrpIdx  = fi(["mrp","selling price","price","rate","sp"]);
      const costIdx = fi(["cost","cost price","purchase price","cp"]);
      const locIdx  = fi(["location","rack","shelf","bin","position"]);

      if (skuIdx < 0 || nameIdx < 0) {
        toast("Excel must have SKU and Product Name columns", "error");
        setExcelUploading(false); return;
      }

      const errors = [];
      const rows = raw.slice(1).filter((r) => r.some((c) => String(c).trim())).map((r, i) => {
        const sku  = String(r[skuIdx]||"").trim();
        const name = String(r[nameIdx]||"").trim();
        if (!sku)  errors.push("Row "+(i+2)+": SKU is empty");
        if (!name) errors.push("Row "+(i+2)+": Product name is empty");
        return {
          sku,
          productName: name,
          category:    catIdx  >= 0 ? String(r[catIdx]||"").trim()  : "",
          hsn:         hsnIdx  >= 0 ? String(r[hsnIdx]||"").trim()  : "",
          unit:        unitIdx >= 0 ? String(r[unitIdx]||"pcs").trim() : "pcs",
          qty:         qtyIdx  >= 0 ? parseInt(r[qtyIdx])||0         : 0,
          minQty:      minIdx  >= 0 ? parseInt(r[minIdx])||0          : 0,
          mrp:         mrpIdx  >= 0 ? parseFloat(r[mrpIdx])||0        : 0,
          costPrice:   costIdx >= 0 ? parseFloat(r[costIdx])||0       : 0,
          location:    locIdx  >= 0 ? String(r[locIdx]||"").trim()   : "",
          notes:       "",
          warehouseId,
          _valid: !!sku && !!name,
        };
      }).filter((r) => r.sku || r.productName);

      setExcelPreview({ rows, warehouseId, errors });
      toast(`Parsed ${rows.length} rows from ${file.name}`, "success");
    } catch(e) { toast("Failed to read file: "+e.message, "error"); }
    setExcelUploading(false);
  };

  const importStockFromExcel = async () => {
    if (!excelPreview) return;
    const validRows = excelPreview.rows.filter((r) => r._valid);
    if (!validRows.length) { toast("No valid rows to import", "error"); return; }
    try {
      toast(`${excelMode === "replace" ? "Importing" : excelMode === "add" ? "Adding to" : "Subtracting from"} ${validRows.length} items…`, "success");
      if (excelMode === "replace") {
        // Original behavior — upsert (set qty)
        const data = await api("/wms/stock/bulk", { method:"POST", body:JSON.stringify({ items: validRows }) });
        toast(`Imported ${data.imported} items${data.failed?" ("+data.failed+" failed)":""} ✓`, "success");
      } else {
        // Adjust mode — add or subtract qty for existing SKUs
        let ok = 0, notFound = 0, fail = 0;
        for (const row of validRows) {
          try {
            // Find existing stock entry for this SKU in this warehouse
            const existing = stock.find((s) =>
              s.sku.toLowerCase() === row.sku.toLowerCase() &&
              s.warehouseId === (row.warehouseId || excelPreview.warehouseId)
            );
            if (!existing) { notFound++; continue; }
            const adjustment = excelMode === "add" ? (row.qty || 0) : -(row.qty || 0);
            await api("/wms/stock/"+existing._id+"/adjust", {
              method: "PUT",
              body: JSON.stringify({ adjustment, reason: "Excel "+excelMode+" import" })
            });
            ok++;
          } catch { fail++; }
        }
        const msg = `${excelMode === "add" ? "Added to" : "Subtracted from"} ${ok} items` +
          (notFound ? ` · ${notFound} SKUs not found in warehouse` : "") +
          (fail ? ` · ${fail} failed` : "") + " ✓";
        toast(msg, "success");
      }
      setExcelPreview(null);
      loadAll();
    } catch(e) { toast("Import failed: "+e.message, "error"); }
  };

  const downloadSampleExcel = () => {
    const wb = XLSX.utils.book_new();
    const data = [
      ["SKU *","Product Name *","Category","HSN Code","Unit","Qty *","Min Qty","MRP (₹)","Cost Price (₹)","Rack/Location","Notes"],
      ["LP-BLK-001","Leather Black Belt 34","Belts","42033000","pcs",50,10,499,280,"A-1-2","Premium leather"],
      ["LP-BRN-002","Leather Brown Belt 36","Belts","42033000","pcs",30,5,499,280,"A-1-3",""],
      ["LP-TAN-003","Leather Tan Belt 38","Belts","42033000","pcs",0,5,549,300,"A-1-4","Out of stock"],
      ["PKG-BOX-001","Packaging Box Small","Packaging","48194000","pcs",200,50,12,8,"B-3-1",""],
      ["PKG-BAG-001","Poly Bag 6x8","Packaging","39232100","pcs",500,100,2,1,"B-3-2",""],
    ];
    const ws = XLSX.utils.aoa_to_sheet(data);
    ws["!cols"] = [{wch:16},{wch:32},{wch:14},{wch:12},{wch:8},{wch:8},{wch:8},{wch:10},{wch:14},{wch:14},{wch:20}];
    // Style header row instruction
    XLSX.utils.book_append_sheet(wb, ws, "Stock Upload");
    // Instructions sheet
    const instData = [
      ["SELLSYNC STOCK UPLOAD — INSTRUCTIONS"],
      [""],
      ["Required columns (marked with *):"],
      ["SKU *","Unique product code e.g. LP-BLK-001"],
      ["Product Name *","Full product name"],
      [""],
      ["Optional columns:"],
      ["Category","e.g. Belts, Packaging, Raw Material"],
      ["HSN Code","GST HSN code e.g. 42033000"],
      ["Unit","pcs / kg / m / box / dozen (default: pcs)"],
      ["Qty","Current stock quantity (default: 0)"],
      ["Min Qty","Minimum stock before low-stock alert (default: 0)"],
      ["MRP (₹)","Maximum Retail Price"],
      ["Cost Price (₹)","Your purchase cost per unit"],
      ["Rack/Location","Physical location e.g. A-1-2 (Aisle-Rack-Shelf)"],
      ["Notes","Any additional notes"],
      [""],
      ["NOTES:"],
      ["- Columns can be in any order — system auto-detects by header name"],
      ["- If SKU already exists in the warehouse, qty will be updated"],
      ["- Delete this instructions sheet before uploading if it causes issues"],
    ];
    const ws2 = XLSX.utils.aoa_to_sheet(instData);
    ws2["!cols"] = [{wch:20},{wch:60}];
    XLSX.utils.book_append_sheet(wb, ws2, "Instructions");
    XLSX.writeFile(wb, "3June_Stock_Upload_Template.xlsx");
    toast("Sample Excel downloaded ✓", "success");
  };

  // ── stock barcode scan ────────────────────────────────────────
  const handleStockScan = (barcode) => {
    const trimmed = barcode.trim();
    if (!trimmed) return;
    const found = stock.find((s) => s.sku.toLowerCase() === trimmed.toLowerCase());
    if (found) {
      setStockScanResult(found);
      setStockScanQty(found.qty);
      setStockScanAdj("set");
    } else {
      setStockScanResult({ _notFound: true, sku: trimmed });
    }
    setStockScanInput("");
  };

  const applyStockScanUpdate = async () => {
    if (!stockScanResult || stockScanResult._notFound) return;
    try {
      let newQty = stockScanResult.qty;
      if (stockScanAdj === "set")      newQty = stockScanQty;
      if (stockScanAdj === "add")      newQty = stockScanResult.qty + stockScanQty;
      if (stockScanAdj === "subtract") newQty = Math.max(0, stockScanResult.qty - stockScanQty);
      await api("/wms/stock/"+stockScanResult._id, { method:"PUT", body:JSON.stringify({ qty: newQty }) });
      toast("Stock updated to "+newQty+" ✓", "success");
      setStockScanResult(null);
      setStockScanInput("");
      loadAll();
    } catch(e) { toast(e.message, "error"); }
  };

  // ── barcode scan ─────────────────────────────────────────────
  const handleScan = (barcode) => {
    const trimmed = barcode.trim();
    if (!trimmed) return;
    const found = products.find((p) =>
      p.shopifySku === trimmed || p.name.toLowerCase().includes(trimmed.toLowerCase())
    );
    if (found) {
      setScanResult(found);
      toast("Product found: " + found.name, "success");
    } else {
      setScanResult(null);
      toast("No product found for: " + trimmed, "error");
    }
    setScanInput("");
  };

  useEffect(() => {
    if (scanMode && scanRef.current) scanRef.current.focus();
  }, [scanMode]);

  // ── warehouse CRUD ───────────────────────────────────────────
  const saveWarehouse = async () => {
    if (!whForm.name) { toast("Enter warehouse name", "error"); return; }
    try {
      if (editWh) {
        await api("/wms/warehouses/" + editWh._id, { method:"PUT", body:JSON.stringify(whForm) });
        toast("Warehouse updated ✓", "success");
      } else {
        await api("/wms/warehouses", { method:"POST", body:JSON.stringify(whForm) });
        toast("Warehouse added ✓", "success");
      }
      setWhModal(false); setEditWh(null); setWhForm(EMPTY_WH); loadAll();
    } catch(e) { toast(e.message, "error"); }
  };

  const setPrimary = async (id) => {
    try {
      await api("/wms/warehouses/" + id + "/primary", { method:"PUT", body:JSON.stringify({}) });
      toast("Primary warehouse updated ✓", "success"); loadAll();
    } catch(e) { toast(e.message, "error"); }
  };

  const deleteWarehouse = async (id) => {
    try {
      await api("/wms/warehouses/" + id, { method:"DELETE" });
      toast("Warehouse removed ✓", "success"); loadAll();
    } catch(e) { toast(e.message, "error"); }
  };

  // ── transfer CRUD ────────────────────────────────────────────
  const createTransfer = async () => {
    if (!trfForm.fromWarehouse || !trfForm.toWarehouse) { toast("Select both warehouses", "error"); return; }
    if (trfForm.items.some((i) => !i.productName)) { toast("Fill all product names", "error"); return; }
    try {
      await api("/wms/transfers", { method:"POST", body:JSON.stringify(trfForm) });
      toast("Transfer created ✓", "success"); setTrfModal(false); setTrfForm(EMPTY_TRF); loadAll();
    } catch(e) { toast(e.message, "error"); }
  };

  const updateTransferStatus = async (id, status) => {
    try {
      await api("/wms/transfers/" + id + "/status", { method:"PUT", body:JSON.stringify({ status }) });
      toast("Transfer status updated ✓", "success"); loadAll();
    } catch(e) { toast(e.message, "error"); }
  };

  // ── picklist CRUD ────────────────────────────────────────────
  const createPicklist = async () => {
    if (!pckForm.orderNumber) { toast("Enter order number", "error"); return; }
    if (pckForm.items.some((i) => !i.productName)) { toast("Fill all product names", "error"); return; }
    try {
      const wh = warehouses.find((w) => w._id === pckForm.warehouseId);
      await api("/wms/picklists", { method:"POST", body:JSON.stringify({ ...pckForm, warehouseName: wh?.name || "" }) });
      toast("Picklist created ✓", "success"); setPckModal(false); setPckForm(EMPTY_PCK); loadAll();
    } catch(e) { toast(e.message, "error"); }
  };

  const toggleItem = async (picklistId, itemIndex, field, value) => {
    try {
      const body = { itemIndex };
      body[field] = value;
      if (field === "isPicked") body.pickedQty = value ? activePck?.items[itemIndex]?.orderedQty : 0;
      if (field === "isPacked") body.packedQty = value ? activePck?.items[itemIndex]?.orderedQty : 0;
      const data = await api("/wms/picklists/" + picklistId + "/item", { method:"PUT", body:JSON.stringify(body) });
      setActivePck(data.picklist);
      setPicklists((prev) => prev.map((p) => p._id === picklistId ? data.picklist : p));
    } catch(e) { toast(e.message, "error"); }
  };

  const updatePickStatus = async (id, status) => {
    try {
      await api("/wms/picklists/" + id + "/status", { method:"PUT", body:JSON.stringify({ status }) });
      toast("Status updated ✓", "success"); loadAll(); setActivePck(null);
    } catch(e) { toast(e.message, "error"); }
  };

  // ── auto replenishment ───────────────────────────────────────
  const createReplenishPO = async () => {
    if (!repSupplier.name) { toast("Enter supplier name", "error"); return; }
    try {
      await api("/wms/replenish", { method:"POST", body:JSON.stringify({ items:repItems, supplierName:repSupplier.name, supplierEmail:repSupplier.email }) });
      toast("Purchase order created ✓", "success"); setRepModal(false);
    } catch(e) { toast(e.message, "error"); }
  };

  // ── stats ────────────────────────────────────────────────────
  const criticalItems   = forecast.filter((f) => ["out_of_stock","critical"].includes(f.status));
  const needsReorderItems = forecast.filter((f) => f.needsReorder);
  const activeTransfers = transfers.filter((t) => t.status === "in_transit").length;
  const pendingPicks    = picklists.filter((p) => ["pending","picking","picked","packing"].includes(p.status)).length;

  return (
    <div>
      {/* KPI row */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12, marginBottom:14 }}>
        {[
          { label:"Warehouses",       value:warehouses.length,  color:"#2563eb" },
          { label:"Active Transfers", value:activeTransfers,    color:"#d97706" },
          { label:"Pending Picks",    value:pendingPicks,       color:"#7c3aed" },
          { label:"Critical Stock",   value:criticalItems.length,color:"#dc2626" },
        ].map((k) => (
          <div key={k.label} style={S.kpi}>
            <div style={{ fontSize:11, color:"#6b7280", marginBottom:6 }}>{k.label}</div>
            <div style={{ fontSize:22, fontWeight:700, color:k.color }}>{k.value}</div>
          </div>
        ))}
      </div>

      {/* tabs */}
      <div style={{ display:"flex", borderBottom:"1px solid #e5e7eb", marginBottom:16, flexWrap:"wrap" }}>
        {[
          { id:"warehouses", label:"🏭 Warehouses"      },
          { id:"transfers",  label:"🔄 Stock Transfers"  },
          { id:"picklist",   label:"📋 Pick & Pack"      },
          { id:"forecast",   label:"📈 Forecasting"      },
          { id:"scanner",    label:"🔍 Barcode Scanner"  },
          { id:"stock",      label:"📦 Warehouse Stock"  },
        ].map((t) => (
          <button key={t.id} style={S.tab(tab===t.id)} onClick={() => setTab(t.id)}>{t.label}</button>
        ))}
      </div>

      {/* ══ WAREHOUSES ══ */}
      {tab === "warehouses" && (
        <>
          <div style={{ display:"flex", justifyContent:"flex-end", marginBottom:14 }}>
            <button style={S.btn()} onClick={() => { setEditWh(null); setWhForm(EMPTY_WH); setWhModal(true); }}>+ Add Warehouse</button>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(280px,1fr))", gap:14 }}>
            {warehouses.map((wh) => (
              <div key={wh._id} style={{ ...S.card, marginBottom:0, borderTop:`3px solid ${wh.isPrimary?"#F47920":"#e5e7eb"}` }}>
                <div style={{ padding:16 }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:10 }}>
                    <div>
                      <div style={{ fontWeight:700, fontSize:15 }}>{wh.name}</div>
                      <div style={{ fontFamily:"monospace", fontSize:11, color:"#9ca3af" }}>{wh.code}</div>
                    </div>
                    {wh.isPrimary && <Badge label="Primary" color="#F47920" bg="#fff3ea"/>}
                  </div>
                  {[
                    ["📍", [wh.city, wh.state, wh.pincode].filter(Boolean).join(", ")],
                    ["👤", wh.manager],
                    ["📞", wh.phone],
                  ].filter(([,v]) => v).map(([icon, val]) => (
                    <div key={icon} style={{ fontSize:12, color:"#6b7280", marginBottom:4, display:"flex", gap:6 }}>
                      <span>{icon}</span><span>{val}</span>
                    </div>
                  ))}
                  <div style={{ display:"flex", gap:6, marginTop:12 }}>
                    <button style={{ ...S.btnOut, fontSize:11 }} onClick={() => { setEditWh(wh); setWhForm({ name:wh.name, code:wh.code, address:wh.address, city:wh.city, state:wh.state, pincode:wh.pincode, phone:wh.phone, manager:wh.manager, shopifyLocationId:wh.shopifyLocationId, notes:wh.notes }); setWhModal(true); }}>Edit</button>
                    {!wh.isPrimary && <button style={{ ...S.btn("#2563eb"), fontSize:11, padding:"4px 10px" }} onClick={() => setPrimary(wh._id)}>Set Primary</button>}
                    {!wh.isPrimary && <button style={{ ...S.btnOut, fontSize:11, color:"#dc2626", borderColor:"#fecaca" }} onClick={() => deleteWarehouse(wh._id)}>Remove</button>}
                  </div>
                </div>
              </div>
            ))}
            {warehouses.length === 0 && (
              <div style={{ gridColumn:"1/-1", textAlign:"center", padding:60, color:"#9ca3af" }}>
                <div style={{ fontSize:40, marginBottom:12 }}>🏭</div>
                No warehouses yet — add your first one
              </div>
            )}
          </div>
        </>
      )}

      {/* ══ TRANSFERS ══ */}
      {tab === "transfers" && (
        <>
          <div style={{ display:"flex", justifyContent:"flex-end", marginBottom:14 }}>
            <button style={S.btn()} onClick={() => { setTrfForm(EMPTY_TRF); setTrfModal(true); }}>+ New Transfer</button>
          </div>
          <div style={S.card}>
            <div style={{ overflowX:"auto" }}>
              <table style={{ width:"100%", borderCollapse:"collapse" }}>
                <thead><tr>
                  {["Transfer #","From","To","Items","Status","Dispatched","Received","Actions"].map((h) => <th key={h} style={S.th}>{h}</th>)}
                </tr></thead>
                <tbody>
                  {transfers.map((t) => {
                    const st  = STATUS_COLORS[t.status] || STATUS_COLORS.draft;
                    const flow = { draft:"in_transit", in_transit:"received" };
                    const nextStatus = flow[t.status];
                    return (
                      <tr key={t._id} onMouseEnter={(e) => e.currentTarget.style.background="#fafafa"} onMouseLeave={(e) => e.currentTarget.style.background=""}>
                        <td style={{ ...S.td, fontFamily:"monospace", fontSize:11, color:"#F47920" }}>{t.transferNumber}</td>
                        <td style={{ ...S.td, fontWeight:500 }}>{t.fromName}</td>
                        <td style={{ ...S.td, fontWeight:500 }}>{t.toName}</td>
                        <td style={S.td}>{t.items.length} item{t.items.length!==1?"s":""}</td>
                        <td style={S.td}><Badge label={t.status.replace("_"," ")} color={st.color} bg={st.bg}/></td>
                        <td style={{ ...S.td, fontSize:11, color:"#6b7280" }}>{t.dispatchedAt ? new Date(t.dispatchedAt).toLocaleDateString("en-IN") : "—"}</td>
                        <td style={{ ...S.td, fontSize:11, color:"#6b7280" }}>{t.receivedAt ? new Date(t.receivedAt).toLocaleDateString("en-IN") : "—"}</td>
                        <td style={S.td}>
                          {nextStatus && t.status !== "cancelled" && (
                            <button style={{ ...S.btn("#2563eb"), fontSize:11, padding:"3px 8px" }} onClick={() => updateTransferStatus(t._id, nextStatus)}>
                              → {nextStatus.replace("_"," ")}
                            </button>
                          )}
                          {t.status === "draft" && (
                            <button style={{ ...S.btnOut, fontSize:11, color:"#dc2626", borderColor:"#fecaca", marginLeft:4 }} onClick={() => updateTransferStatus(t._id, "cancelled")}>Cancel</button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {transfers.length === 0 && (
                    <tr><td colSpan={8} style={{ ...S.td, textAlign:"center", color:"#9ca3af", padding:40 }}>No transfers yet</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* ══ PICK & PACK ══ */}
      {tab === "picklist" && (
        <div style={{ display:"grid", gridTemplateColumns: activePck ? "1fr 1fr" : "1fr", gap:14 }}>
          <div>
            <div style={{ display:"flex", justifyContent:"flex-end", marginBottom:14 }}>
              <button style={S.btn()} onClick={() => { setPckForm(EMPTY_PCK); setPckModal(true); }}>+ New Picklist</button>
            </div>
            <div style={S.card}>
              <div style={{ overflowX:"auto" }}>
                <table style={{ width:"100%", borderCollapse:"collapse" }}>
                  <thead><tr>
                    {["Pick #","Order","Customer","Warehouse","Items","Status",""].map((h) => <th key={h} style={S.th}>{h}</th>)}
                  </tr></thead>
                  <tbody>
                    {picklists.map((p) => {
                      const st = STATUS_COLORS[p.status] || STATUS_COLORS.pending;
                      const picked = p.items.filter((i) => i.isPicked).length;
                      const packed = p.items.filter((i) => i.isPacked).length;
                      return (
                        <tr key={p._id} onMouseEnter={(e) => e.currentTarget.style.background="#fafafa"} onMouseLeave={(e) => e.currentTarget.style.background=""}>
                          <td style={{ ...S.td, fontFamily:"monospace", fontSize:11, color:"#F47920" }}>{p.pickNumber}</td>
                          <td style={{ ...S.td, fontSize:11, color:"#6b7280" }}>{p.orderNumber}</td>
                          <td style={{ ...S.td, fontWeight:500 }}>{p.customerName || "—"}</td>
                          <td style={{ ...S.td, fontSize:11 }}>{p.warehouseName || "—"}</td>
                          <td style={S.td}>
                            <div style={{ fontSize:11 }}>{picked}/{p.items.length} picked</div>
                            <div style={{ fontSize:11, color:"#6b7280" }}>{packed}/{p.items.length} packed</div>
                          </td>
                          <td style={S.td}><Badge label={p.status} color={st.color} bg={st.bg}/></td>
                          <td style={S.td}>
                            <button style={{ ...S.btn(), fontSize:11, padding:"3px 8px" }} onClick={() => setActivePck(p)}>
                              {p.status === "pending" ? "Start Pick" : "View"}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                    {picklists.length === 0 && (
                      <tr><td colSpan={7} style={{ ...S.td, textAlign:"center", color:"#9ca3af", padding:40 }}>No picklists yet</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* active picklist panel */}
          {activePck && (
            <div style={S.card}>
              <div style={S.cardHdr}>
                <div>
                  <span style={{ fontWeight:700 }}>{activePck.pickNumber}</span>
                  <span style={{ fontSize:11, color:"#9ca3af", marginLeft:8 }}>{activePck.orderNumber}</span>
                </div>
                <button onClick={() => setActivePck(null)} style={{ background:"none", border:"none", fontSize:18, cursor:"pointer", color:"#9ca3af" }}>✕</button>
              </div>
              <div style={{ padding:16 }}>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:16 }}>
                  {[["Customer",activePck.customerName||"—"],["Warehouse",activePck.warehouseName||"—"],["Status",activePck.status],["Assigned",activePck.assignedTo||"—"]].map(([l,v])=>(
                    <div key={l} style={{ background:"#f9fafb", borderRadius:7, padding:"8px 10px" }}>
                      <div style={{ fontSize:10, color:"#9ca3af", marginBottom:2 }}>{l}</div>
                      <div style={{ fontWeight:600, fontSize:13 }}>{v}</div>
                    </div>
                  ))}
                </div>

                <div style={{ fontWeight:600, fontSize:13, marginBottom:10 }}>Items</div>
                {activePck.items.map((item, i) => (
                  <div key={i} style={{ background: item.isPacked?"#f0fdf4":item.isPicked?"#eff6ff":"#f9fafb", borderRadius:8, padding:12, marginBottom:8, border:`1px solid ${item.isPacked?"#bbf7d0":item.isPicked?"#bfdbfe":"#e5e7eb"}` }}>
                    <div style={{ display:"flex", justifyContent:"space-between", marginBottom:8 }}>
                      <div>
                        <div style={{ fontWeight:600, fontSize:13 }}>{item.productName}</div>
                        <div style={{ fontSize:11, color:"#6b7280" }}>{item.shopifySku} · {item.location || "No location set"}</div>
                      </div>
                      <div style={{ fontWeight:700, fontSize:16, color:"#F47920" }}>×{item.orderedQty}</div>
                    </div>
                    <div style={{ display:"flex", gap:8 }}>
                      <button
                        style={{ ...S.btn(item.isPicked?"#9ca3af":"#2563eb"), fontSize:11, padding:"4px 12px", flex:1 }}
                        onClick={() => toggleItem(activePck._id, i, "isPicked", !item.isPicked)}>
                        {item.isPicked ? "✓ Picked" : "Mark Picked"}
                      </button>
                      <button
                        style={{ ...S.btn(item.isPacked?"#9ca3af":"#15803d"), fontSize:11, padding:"4px 12px", flex:1 }}
                        disabled={!item.isPicked}
                        onClick={() => toggleItem(activePck._id, i, "isPacked", !item.isPacked)}>
                        {item.isPacked ? "✓ Packed" : "Mark Packed"}
                      </button>
                    </div>
                  </div>
                ))}

                {/* action buttons */}
                <div style={{ display:"flex", gap:8, marginTop:14 }}>
                  {activePck.status === "pending" && (
                    <button style={{ ...S.btn("#2563eb"), flex:1 }} onClick={() => updatePickStatus(activePck._id, "picking")}>Start Picking</button>
                  )}
                  {activePck.status === "packed" && (
                    <button style={{ ...S.btn("#15803d"), flex:1 }} onClick={() => updatePickStatus(activePck._id, "dispatched")}>Mark Dispatched</button>
                  )}
                  {!["dispatched","cancelled"].includes(activePck.status) && (
                    <button style={{ ...S.btnOut, color:"#dc2626", borderColor:"#fecaca" }} onClick={() => updatePickStatus(activePck._id, "cancelled")}>Cancel</button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ══ FORECASTING ══ */}
      {tab === "forecast" && (
        <>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14, flexWrap:"wrap", gap:8 }}>
            <div style={{ fontSize:13, color:"#6b7280" }}>
              Based on last 30 days sales velocity across all platforms · {forecast.length} products analysed
              {Object.keys(forecastSources).length > 0 && (
                <span style={{ marginLeft:8 }}>
                  {Object.entries(forecastSources).filter(([,v])=>v>0).map(([pl,count])=>(
                    <span key={pl} style={{ background:"#f3f4f6", borderRadius:10, padding:"1px 7px", fontSize:10, marginRight:4, fontWeight:600 }}>
                      {pl==="shopify"?"🏪":pl==="amazon"?"📦":pl==="flipkart"?"🛒":pl==="myntra"?"👗":"🛍️"} {count}
                    </span>
                  ))}
                </span>
              )}
            </div>
            <div style={{ display:"flex", gap:8 }}>
              {forecast.length > 0 && (
                <button style={{ ...S.btn("#6366f1"), opacity:aiLoading?0.7:1 }} onClick={generateAiSuggestions} disabled={aiLoading}>
                  {aiLoading ? "🤖 Analysing…" : "🤖 AI Reorder Suggestions"}
                </button>
              )}
              {needsReorderItems.length > 0 && (
                <button style={{ ...S.btn("#dc2626") }} onClick={() => { setRepItems(needsReorderItems); setRepModal(true); }}>
                  🔁 Quick Replenish {needsReorderItems.length}
                </button>
              )}
              <button style={{ ...S.btn(), opacity:forecasting?0.7:1 }} onClick={runForecast} disabled={forecasting}>
                {forecasting ? "Forecasting…" : "↻ Refresh"}
              </button>
            </div>
          </div>

          <div style={S.card}>
            <div style={{ overflowX:"auto" }}>
              <table style={{ width:"100%", borderCollapse:"collapse" }}>
                <thead><tr>
                  {["Product","SKU","Total Stock","Daily Sales","Days Left","Reorder Point","Suggested Order","Status"].map((h) => <th key={h} style={S.th}>{h}</th>)}
                </tr></thead>
                <tbody>
                  {forecast.map((f, i) => {
                    const st = STATUS_COLORS[f.status] || STATUS_COLORS.healthy;
                    return (
                      <tr key={i} onMouseEnter={(e) => e.currentTarget.style.background="#fafafa"} onMouseLeave={(e) => e.currentTarget.style.background=""}>
                        <td style={{ ...S.td, fontWeight:500, maxWidth:180, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{f.name}</td>
                        <td style={{ ...S.td, fontFamily:"monospace", fontSize:11, color:"#6b7280" }}>{f.sku}</td>
                        <td style={{ ...S.td, fontWeight:600, color:f.totalStock===0?"#dc2626":"#111" }}>{f.totalStock}</td>
                        <td style={S.td}>{f.dailyVelocity > 0 ? f.dailyVelocity + "/day" : <span style={{ color:"#9ca3af" }}>No data</span>}</td>
                        <td style={{ ...S.td, fontWeight:600, color:f.daysLeft===null?"#9ca3af":f.daysLeft<=7?"#dc2626":f.daysLeft<=14?"#d97706":"#15803d" }}>
                          {f.daysLeft === null ? "—" : f.daysLeft + " days"}
                        </td>
                        <td style={S.td}>{f.reorderPoint > 0 ? f.reorderPoint + " units" : "—"}</td>
                        <td style={{ ...S.td, fontWeight:600, color:f.suggestedOrder>0?"#F47920":"#9ca3af" }}>
                          {f.suggestedOrder > 0 ? f.suggestedOrder + " units" : "—"}
                        </td>
                        <td style={S.td}><Badge label={f.status.replace("_"," ")} color={st.color} bg={st.bg}/></td>
                      </tr>
                    );
                  })}
                  {forecast.length === 0 && (
                    <tr><td colSpan={8} style={{ ...S.td, textAlign:"center", color:"#9ca3af", padding:40 }}>
                      {products.length === 0 ? "Sync products first to see forecast" : "Running forecast…"}
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* ══ BARCODE SCANNER ══ */}
      {tab === "scanner" && (
        <div style={{ maxWidth:600, margin:"0 auto" }}>
          <div style={S.card}>
            <div style={S.cardHdr}><span>🔍 Barcode / SKU Scanner</span></div>
            <div style={{ padding:20 }}>
              <div style={{ background:"#f9fafb", borderRadius:10, padding:20, marginBottom:16, textAlign:"center" }}>
                <div style={{ fontSize:48, marginBottom:12 }}>📷</div>
                <div style={{ fontWeight:600, fontSize:15, marginBottom:6 }}>Scan or type barcode / SKU</div>
                <div style={{ fontSize:12, color:"#6b7280", marginBottom:16 }}>
                  Connect a USB/Bluetooth barcode scanner or type the SKU manually.
                  Scanner input auto-submits after reading.
                </div>
                <input
                  ref={scanRef}
                  style={{ ...S.input, textAlign:"center", fontSize:16, fontFamily:"monospace", letterSpacing:2, marginBottom:0 }}
                  placeholder="Scan barcode or type SKU…"
                  value={scanInput}
                  autoFocus
                  onChange={(e) => setScanInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleScan(scanInput);
                  }}
                />
                <div style={{ fontSize:11, color:"#9ca3af", marginTop:8 }}>Press Enter or scan barcode to search</div>
              </div>

              {/* scan result */}
              {scanResult && (
                <div style={{ background:"#f0fdf4", border:"1px solid #bbf7d0", borderRadius:10, padding:16 }}>
                  <div style={{ fontWeight:700, fontSize:15, color:"#15803d", marginBottom:12 }}>✓ Product Found</div>
                  <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:12 }}>
                    {scanResult.imageUrl
                      ? <img src={scanResult.imageUrl} alt="" style={{ width:56, height:56, borderRadius:8, objectFit:"cover" }}/>
                      : <div style={{ width:56, height:56, borderRadius:8, background:"#e5e7eb", display:"flex", alignItems:"center", justifyContent:"center", fontSize:24 }}>📦</div>
                    }
                    <div>
                      <div style={{ fontWeight:600, fontSize:15 }}>{scanResult.name}</div>
                      <div style={{ fontFamily:"monospace", fontSize:12, color:"#6b7280" }}>{scanResult.shopifySku}</div>
                    </div>
                  </div>
                  <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:8 }}>
                    {[
                      { label:"Shopify",  value:scanResult.inventory?.shopify  || 0, color:"#5a8a00" },
                      { label:"Amazon",   value:scanResult.inventory?.amazon   || 0, color:"#c45000" },
                      { label:"Flipkart", value:scanResult.inventory?.flipkart || 0, color:"#1b57c0" },
                      { label:"Myntra",   value:scanResult.inventory?.myntra   || 0, color:"#b5003c" },
                    ].map((p) => (
                      <div key={p.label} style={{ background:"#fff", borderRadius:8, padding:"8px 10px", textAlign:"center", border:"1px solid #e5e7eb" }}>
                        <div style={{ fontSize:10, color:"#9ca3af", marginBottom:3 }}>{p.label}</div>
                        <div style={{ fontWeight:700, fontSize:18, color:p.value===0?"#dc2626":p.color }}>{p.value}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ display:"flex", justifyContent:"space-between", marginTop:12, fontWeight:600, fontSize:14 }}>
                    <span>Price</span><span style={{ color:"#F47920" }}>₹{Number(scanResult.price||0).toLocaleString("en-IN")}</span>
                  </div>
                  <button style={{ ...S.btn(), width:"100%", marginTop:12 }} onClick={() => setScanResult(null)}>
                    Scan Next
                  </button>
                </div>
              )}

              {/* recent scans info */}
              <div style={{ marginTop:16, padding:14, background:"#fffbeb", borderRadius:8, border:"1px solid #fde68a", fontSize:12, color:"#92400e" }}>
                <div style={{ fontWeight:600, marginBottom:6 }}>💡 How to use barcode scanner</div>
                <div>• <strong>USB scanner:</strong> Plug in and click this field — scanner input submits automatically</div>
                <div style={{ marginTop:4 }}>• <strong>Bluetooth scanner:</strong> Pair scanner, click field, scan barcode</div>
                <div style={{ marginTop:4 }}>• <strong>Manual:</strong> Type SKU and press Enter</div>
                <div style={{ marginTop:4 }}>• <strong>Camera:</strong> Use a barcode scanner app on mobile and copy-paste the code</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══ WAREHOUSE MODAL ══ */}
      {whModal && (
        <div style={S.overlay} onClick={(e) => e.target===e.currentTarget && setWhModal(false)}>
          <div style={S.modal}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
              <span style={{ fontWeight:700, fontSize:16 }}>{editWh ? "Edit Warehouse" : "Add Warehouse"}</span>
              <button onClick={() => setWhModal(false)} style={{ background:"none", border:"none", fontSize:20, cursor:"pointer", color:"#9ca3af" }}>✕</button>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
              {[
                { label:"Warehouse Name *", key:"name",    placeholder:"e.g. Delhi Warehouse"  },
                { label:"Code",             key:"code",    placeholder:"e.g. DEL-01"            },
                { label:"Manager Name",     key:"manager", placeholder:"Manager name"           },
                { label:"Phone",            key:"phone",   placeholder:"+91 98XXXXXXXX"         },
                { label:"Pincode",          key:"pincode", placeholder:"110001"                 },
                { label:"Shopify Location ID", key:"shopifyLocationId", placeholder:"Optional" },
              ].map(({ label, key, placeholder }) => (
                <div key={key}>
                  <label style={S.label}>{label}</label>
                  <input style={S.input} placeholder={placeholder} value={whForm[key]} onChange={(e) => setWhForm((f) => ({ ...f, [key]: e.target.value }))}/>
                </div>
              ))}
              <div>
                <label style={S.label}>City</label>
                <select style={S.input} value={whForm.city} onChange={(e) => setWhForm((f) => ({ ...f, city:e.target.value }))}>
                  {CITIES.map((c) => <option key={c}>{c}</option>)}
                  <option value="Other">Other</option>
                </select>
              </div>
              <div>
                <label style={S.label}>State</label>
                <input style={S.input} value={whForm.state} onChange={(e) => setWhForm((f) => ({ ...f, state:e.target.value }))}/>
              </div>
            </div>
            <label style={S.label}>Address</label>
            <input style={S.input} placeholder="Full address" value={whForm.address} onChange={(e) => setWhForm((f) => ({ ...f, address:e.target.value }))}/>
            <label style={S.label}>Notes</label>
            <textarea style={{ ...S.input, height:60, resize:"vertical" }} value={whForm.notes} onChange={(e) => setWhForm((f) => ({ ...f, notes:e.target.value }))}/>
            <button style={{ ...S.btn(), width:"100%", padding:11, fontSize:14 }} onClick={saveWarehouse}>
              {editWh ? "Update Warehouse" : "Add Warehouse"}
            </button>
          </div>
        </div>
      )}

      {/* ══ TRANSFER MODAL ══ */}
      {trfModal && (
        <div style={S.overlay} onClick={(e) => e.target===e.currentTarget && setTrfModal(false)}>
          <div style={S.modal}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
              <span style={{ fontWeight:700, fontSize:16 }}>New Stock Transfer</span>
              <button onClick={() => setTrfModal(false)} style={{ background:"none", border:"none", fontSize:20, cursor:"pointer", color:"#9ca3af" }}>✕</button>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
              <div>
                <label style={S.label}>From Warehouse *</label>
                <select style={S.input} value={trfForm.fromWarehouse} onChange={(e) => setTrfForm((f) => ({ ...f, fromWarehouse:e.target.value }))}>
                  <option value="">— Select —</option>
                  {warehouses.map((w) => <option key={w._id} value={w._id}>{w.name}</option>)}
                </select>
              </div>
              <div>
                <label style={S.label}>To Warehouse *</label>
                <select style={S.input} value={trfForm.toWarehouse} onChange={(e) => setTrfForm((f) => ({ ...f, toWarehouse:e.target.value }))}>
                  <option value="">— Select —</option>
                  {warehouses.filter((w) => w._id !== trfForm.fromWarehouse).map((w) => <option key={w._id} value={w._id}>{w.name}</option>)}
                </select>
              </div>
              <div>
                <label style={S.label}>Reason</label>
                <select style={S.input} value={trfForm.reason} onChange={(e) => setTrfForm((f) => ({ ...f, reason:e.target.value }))}>
                  {["","Replenishment","Excess stock","Seasonal demand","Customer request","Other"].map((r) => <option key={r}>{r}</option>)}
                </select>
              </div>
              <div>
                <label style={S.label}>Notes</label>
                <input style={S.input} value={trfForm.notes} onChange={(e) => setTrfForm((f) => ({ ...f, notes:e.target.value }))}/>
              </div>
            </div>

            <div style={{ fontWeight:600, fontSize:13, margin:"8px 0 10px" }}>Items to Transfer</div>
            {trfForm.items.map((item, i) => (
              <div key={i} style={{ display:"grid", gridTemplateColumns:"2fr 1fr 1fr auto", gap:8, marginBottom:8, alignItems:"flex-end" }}>
                <div>
                  {i === 0 && <label style={S.label}>Product</label>}
                  <input style={{ ...S.input, marginBottom:0 }} placeholder="Product name" value={item.productName}
                    list="prod-list" onChange={(e) => setTrfForm((f) => ({ ...f, items:f.items.map((it,idx) => idx===i?{...it,productName:e.target.value}:it) }))}/>
                </div>
                <div>
                  {i === 0 && <label style={S.label}>SKU</label>}
                  <input style={{ ...S.input, marginBottom:0 }} placeholder="SKU" value={item.shopifySku}
                    onChange={(e) => setTrfForm((f) => ({ ...f, items:f.items.map((it,idx) => idx===i?{...it,shopifySku:e.target.value}:it) }))}/>
                </div>
                <div>
                  {i === 0 && <label style={S.label}>Qty</label>}
                  <input type="number" min="1" style={{ ...S.input, marginBottom:0 }} value={item.requestedQty}
                    onChange={(e) => setTrfForm((f) => ({ ...f, items:f.items.map((it,idx) => idx===i?{...it,requestedQty:parseInt(e.target.value)||1}:it) }))}/>
                </div>
                <button onClick={() => setTrfForm((f) => ({ ...f, items:f.items.filter((_,idx) => idx!==i) }))}
                  style={{ background:"none", border:"none", color:"#dc2626", cursor:"pointer", fontSize:18, paddingBottom:10 }}>✕</button>
              </div>
            ))}
            <datalist id="prod-list">{products.map((p) => <option key={p.id} value={p.name}/>)}</datalist>
            <button style={{ ...S.btnOut, fontSize:12, marginBottom:16 }} onClick={() => setTrfForm((f) => ({ ...f, items:[...f.items,{productName:"",shopifySku:"",requestedQty:1,unit:"pcs"}] }))}>+ Add Item</button>
            <button style={{ ...S.btn(), width:"100%", padding:11, fontSize:14 }} onClick={createTransfer}>Create Transfer</button>
          </div>
        </div>
      )}

      {/* ══ PICKLIST MODAL ══ */}
      {pckModal && (
        <div style={S.overlay} onClick={(e) => e.target===e.currentTarget && setPckModal(false)}>
          <div style={S.modal}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
              <span style={{ fontWeight:700, fontSize:16 }}>New Picklist</span>
              <button onClick={() => setPckModal(false)} style={{ background:"none", border:"none", fontSize:20, cursor:"pointer", color:"#9ca3af" }}>✕</button>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
              <div>
                <label style={S.label}>Order Number *</label>
                <input style={S.input} placeholder="#1001" value={pckForm.orderNumber} onChange={(e) => setPckForm((f) => ({ ...f, orderNumber:e.target.value }))}/>
              </div>
              <div>
                <label style={S.label}>Platform</label>
                <select style={S.input} value={pckForm.platform} onChange={(e) => setPckForm((f) => ({ ...f, platform:e.target.value }))}>
                  {PLATFORMS.map((p) => <option key={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <label style={S.label}>Customer Name</label>
                <input style={S.input} value={pckForm.customerName} onChange={(e) => setPckForm((f) => ({ ...f, customerName:e.target.value }))}/>
              </div>
              <div>
                <label style={S.label}>Assigned To</label>
                <input style={S.input} placeholder="Staff name" value={pckForm.assignedTo} onChange={(e) => setPckForm((f) => ({ ...f, assignedTo:e.target.value }))}/>
              </div>
              <div style={{ gridColumn:"1/-1" }}>
                <label style={S.label}>Warehouse</label>
                <select style={S.input} value={pckForm.warehouseId} onChange={(e) => setPckForm((f) => ({ ...f, warehouseId:e.target.value }))}>
                  <option value="">— Select warehouse —</option>
                  {warehouses.map((w) => <option key={w._id} value={w._id}>{w.name}</option>)}
                </select>
              </div>
            </div>

            <div style={{ fontWeight:600, fontSize:13, margin:"8px 0 10px" }}>Items to Pick</div>
            {pckForm.items.map((item, i) => (
              <div key={i} style={{ display:"grid", gridTemplateColumns:"2fr 1fr 1fr 1fr auto", gap:8, marginBottom:8, alignItems:"flex-end" }}>
                <div>
                  {i===0 && <label style={S.label}>Product</label>}
                  <input style={{ ...S.input, marginBottom:0 }} placeholder="Product" value={item.productName} list="pl-prod"
                    onChange={(e) => setPckForm((f) => ({ ...f, items:f.items.map((it,idx) => idx===i?{...it,productName:e.target.value}:it) }))}/>
                </div>
                <div>
                  {i===0 && <label style={S.label}>SKU</label>}
                  <input style={{ ...S.input, marginBottom:0 }} value={item.shopifySku}
                    onChange={(e) => setPckForm((f) => ({ ...f, items:f.items.map((it,idx) => idx===i?{...it,shopifySku:e.target.value}:it) }))}/>
                </div>
                <div>
                  {i===0 && <label style={S.label}>Qty</label>}
                  <input type="number" min="1" style={{ ...S.input, marginBottom:0 }} value={item.orderedQty}
                    onChange={(e) => setPckForm((f) => ({ ...f, items:f.items.map((it,idx) => idx===i?{...it,orderedQty:parseInt(e.target.value)||1}:it) }))}/>
                </div>
                <div>
                  {i===0 && <label style={S.label}>Bin/Shelf</label>}
                  <input style={{ ...S.input, marginBottom:0 }} placeholder="A-01" value={item.location}
                    onChange={(e) => setPckForm((f) => ({ ...f, items:f.items.map((it,idx) => idx===i?{...it,location:e.target.value}:it) }))}/>
                </div>
                <button onClick={() => setPckForm((f) => ({ ...f, items:f.items.filter((_,idx) => idx!==i) }))}
                  style={{ background:"none", border:"none", color:"#dc2626", cursor:"pointer", fontSize:18, paddingBottom:10 }}>✕</button>
              </div>
            ))}
            <datalist id="pl-prod">{products.map((p) => <option key={p.id} value={p.name}/>)}</datalist>
            <button style={{ ...S.btnOut, fontSize:12, marginBottom:16 }} onClick={() => setPckForm((f) => ({ ...f, items:[...f.items,{productName:"",shopifySku:"",orderedQty:1,unit:"pcs",location:""}] }))}>+ Add Item</button>
            <button style={{ ...S.btn(), width:"100%", padding:11, fontSize:14 }} onClick={createPicklist}>Create Picklist</button>
          </div>
        </div>
      )}

      {/* ══ AI REORDER SUGGESTIONS PANEL ══ */}
      {aiDone && aiSuggestions.length > 0 && tab === "forecast" && (
        <div style={{ ...S.card, border:"2px solid #6366f1", marginTop:14 }}>
          <div style={{ padding:"12px 16px", background:"linear-gradient(135deg,#6366f1,#8b5cf6)", borderRadius:"8px 8px 0 0", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
            <div>
              <div style={{ fontWeight:700, fontSize:14, color:"#fff" }}>🤖 AI Reorder Suggestions</div>
              <div style={{ fontSize:11, color:"rgba(255,255,255,0.8)", marginTop:2 }}>Based on sales velocity, days of stock remaining and reorder points</div>
            </div>
            <div style={{ display:"flex", gap:8 }}>
              <button style={{ ...S.btnOut, fontSize:11 }} onClick={() => setSelSuggestions(aiSuggestions.map((_,i)=>i))}>Select All</button>
              <button style={{ ...S.btn("#15803d"), fontSize:12 }} onClick={createAiPOs}>
                📦 Create {selSuggestions.length} Purchase Order{selSuggestions.length!==1?"s":""}
              </button>
              <button onClick={() => { setAiDone(false); setAiSuggestions([]); }} style={{ background:"rgba(255,255,255,0.2)", border:"none", color:"#fff", borderRadius:6, padding:"4px 10px", cursor:"pointer", fontSize:18 }}>✕</button>
            </div>
          </div>

          <div style={{ padding:16 }}>
            {/* Urgency summary */}
            <div style={{ display:"flex", gap:8, marginBottom:14, flexWrap:"wrap" }}>
              {["critical","high","medium","low"].map((u) => {
                const count = aiSuggestions.filter((s)=>s.urgency===u).length;
                if (!count) return null;
                const colors = { critical:["#dc2626","#fef2f2"], high:["#ea580c","#fff7ed"], medium:["#d97706","#fffbeb"], low:["#15803d","#f0fdf4"] };
                const [c,bg] = colors[u];
                return <span key={u} style={{ background:bg, color:c, padding:"3px 10px", borderRadius:20, fontSize:11, fontWeight:700 }}>{u.charAt(0).toUpperCase()+u.slice(1)}: {count}</span>;
              })}
              <span style={{ fontSize:11, color:"#6b7280", marginLeft:"auto", alignSelf:"center" }}>Select items → assign supplier → create POs</span>
            </div>

            {/* Suggestion cards */}
            <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
              {aiSuggestions.map((s, i) => {
                const isSel = selSuggestions.includes(i);
                const urgColors = { critical:["#dc2626","#fef2f2"], high:["#ea580c","#fff7ed"], medium:["#d97706","#fffbeb"], low:["#15803d","#f0fdf4"] };
                const [uc, ubg] = urgColors[s.urgency] || urgColors.medium;
                return (
                  <div key={i} style={{ border:"1.5px solid "+(isSel?"#6366f1":"#e5e7eb"), borderRadius:8, padding:"12px 14px", background:isSel?"#f5f3ff":"#fff", cursor:"pointer", transition:"all .15s" }}
                    onClick={() => setSelSuggestions((prev) => prev.includes(i)?prev.filter((x)=>x!==i):[...prev,i])}>
                    <div style={{ display:"flex", gap:12, alignItems:"flex-start" }}>
                      <input type="checkbox" checked={isSel} onChange={()=>{}} onClick={(e)=>e.stopPropagation()} style={{ marginTop:2, width:15, height:15, flexShrink:0, cursor:"pointer" }}/>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:6, flexWrap:"wrap", gap:6 }}>
                          <div>
                            <div style={{ fontWeight:700, fontSize:13 }}>{s.name}</div>
                            <div style={{ fontSize:11, fontFamily:"monospace", color:"#F47920" }}>{s.sku}</div>
                          </div>
                          <div style={{ display:"flex", gap:6, flexShrink:0 }}>
                            <span style={{ background:ubg, color:uc, padding:"2px 9px", borderRadius:20, fontSize:10, fontWeight:700 }}>
                              {s.urgency?.toUpperCase()}
                            </span>
                            <span style={{ background:"#ede9fe", color:"#6366f1", padding:"2px 9px", borderRadius:20, fontSize:10, fontWeight:700 }}>
                              Order by: {s.orderWithin}
                            </span>
                          </div>
                        </div>

                        {/* Stats row */}
                        <div style={{ display:"flex", gap:16, marginBottom:8, flexWrap:"wrap" }}>
                          {[
                            ["Current Stock", s.totalStock+" units", s.totalStock===0?"#dc2626":"#374151"],
                            ["Daily Sales",   (s.dailyVelocity||0)+"/day", "#6b7280"],
                            ["Days Left",     s.daysLeft!=null?s.daysLeft+" days":"—", s.daysLeft!=null&&s.daysLeft<=3?"#dc2626":s.daysLeft!=null&&s.daysLeft<=7?"#d97706":"#15803d"],
                            ["Stockout",      s.estimatedStockoutDate||"—", "#dc2626"],
                          ].map(([l,v,c])=>(
                            <div key={l}>
                              <div style={{ fontSize:9, color:"#9ca3af", textTransform:"uppercase", marginBottom:1 }}>{l}</div>
                              <div style={{ fontSize:12, fontWeight:600, color:c }}>{v}</div>
                            </div>
                          ))}
                        </div>

                        {/* AI reasoning */}
                        <div style={{ background:"#f5f3ff", borderRadius:6, padding:"6px 10px", fontSize:11, color:"#6366f1", marginBottom:10 }}>
                          💡 {s.reasoning}
                        </div>

                        {/* Editable fields */}
                        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:8 }} onClick={(e)=>e.stopPropagation()}>
                          <div>
                            <label style={{ fontSize:10, color:"#6b7280", fontWeight:600, display:"block", marginBottom:2 }}>Order Qty</label>
                            <input type="number" min="1" style={{ width:"100%", padding:"5px 8px", borderRadius:6, border:"1px solid #d1d5db", fontSize:12, fontFamily:"inherit", outline:"none" }}
                              value={s.recommendedQty||s.suggestedOrder||0}
                              onChange={(e)=>setAiSuggestions((prev)=>prev.map((x,idx)=>idx===i?{...x,recommendedQty:parseInt(e.target.value)||0}:x))}/>
                          </div>
                          <div>
                            <label style={{ fontSize:10, color:"#6b7280", fontWeight:600, display:"block", marginBottom:2 }}>Unit Cost (₹)</label>
                            <input type="number" min="0" style={{ width:"100%", padding:"5px 8px", borderRadius:6, border:"1px solid #d1d5db", fontSize:12, fontFamily:"inherit", outline:"none" }}
                              value={s.unitPrice||0}
                              onChange={(e)=>setAiSuggestions((prev)=>prev.map((x,idx)=>idx===i?{...x,unitPrice:parseFloat(e.target.value)||0}:x))}/>
                          </div>
                          <div>
                            <label style={{ fontSize:10, color:"#6b7280", fontWeight:600, display:"block", marginBottom:2 }}>Supplier</label>
                            <select style={{ width:"100%", padding:"5px 8px", borderRadius:6, border:"1px solid #d1d5db", fontSize:11, fontFamily:"inherit" }}
                              value={s.supplierName}
                              onChange={(e)=>{
                                const sup = suppliers.find((sp)=>sp.name===e.target.value);
                                setAiSuggestions((prev)=>prev.map((x,idx)=>idx===i?{...x,supplierName:e.target.value,supplierEmail:sup?.email||""}:x));
                              }}>
                              <option value="">— Select —</option>
                              {suppliers.map((sp)=><option key={sp._id} value={sp.name}>{sp.name}</option>)}
                              <option value="manual">+ Enter manually</option>
                            </select>
                          </div>
                        </div>
                        {s.supplierName==="manual" && (
                          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginTop:8 }} onClick={(e)=>e.stopPropagation()}>
                            <input placeholder="Supplier name" style={{ padding:"5px 8px", borderRadius:6, border:"1px solid #d1d5db", fontSize:12, fontFamily:"inherit" }}
                              onChange={(e)=>setAiSuggestions((prev)=>prev.map((x,idx)=>idx===i?{...x,supplierName:e.target.value}:x))}/>
                            <input placeholder="Supplier email" type="email" style={{ padding:"5px 8px", borderRadius:6, border:"1px solid #d1d5db", fontSize:12, fontFamily:"inherit" }}
                              onChange={(e)=>setAiSuggestions((prev)=>prev.map((x,idx)=>idx===i?{...x,supplierEmail:e.target.value}:x))}/>
                          </div>
                        )}

                        {/* Estimated PO value */}
                        {s.unitPrice > 0 && (s.recommendedQty||s.suggestedOrder) > 0 && (
                          <div style={{ marginTop:8, fontSize:11, color:"#15803d", fontWeight:600 }}>
                            Estimated PO Value: ₹{((s.recommendedQty||s.suggestedOrder||0) * (s.unitPrice||0)).toLocaleString("en-IN")}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Total summary */}
            {selSuggestions.length > 0 && (
              <div style={{ marginTop:14, background:"#f5f3ff", borderRadius:8, padding:"12px 16px", display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:10 }}>
                <div>
                  <div style={{ fontWeight:700, color:"#6366f1" }}>{selSuggestions.length} items selected</div>
                  <div style={{ fontSize:11, color:"#6b7280" }}>
                    Total units to order: {aiSuggestions.filter((_,i)=>selSuggestions.includes(i)).reduce((a,s)=>a+(s.recommendedQty||s.suggestedOrder||0),0)} ·
                    Est. value: ₹{aiSuggestions.filter((_,i)=>selSuggestions.includes(i)).reduce((a,s)=>a+(s.unitPrice||0)*(s.recommendedQty||s.suggestedOrder||0),0).toLocaleString("en-IN")}
                  </div>
                </div>
                <button style={{ ...S.btn("#15803d"), padding:"10px 20px", fontSize:13 }} onClick={createAiPOs}>
                  📦 Create Purchase Order{selSuggestions.length!==1?"s":""}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ══ REPLENISHMENT MODAL ══ */}
      {repModal && (
        <div style={S.overlay} onClick={(e) => e.target===e.currentTarget && setRepModal(false)}>
          <div style={{ ...S.modal, width:520 }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
              <span style={{ fontWeight:700, fontSize:16 }}>Auto Replenishment</span>
              <button onClick={() => setRepModal(false)} style={{ background:"none", border:"none", fontSize:20, cursor:"pointer", color:"#9ca3af" }}>✕</button>
            </div>
            <div style={{ background:"#fef2f2", border:"1px solid #fecaca", borderRadius:8, padding:12, marginBottom:16, fontSize:13, color:"#b91c1c" }}>
              ⚠️ {repItems.length} products need restocking. A purchase order will be created automatically.
            </div>

            <div style={{ marginBottom:16 }}>
              {repItems.map((item, i) => (
                <div key={i} style={{ display:"flex", justifyContent:"space-between", padding:"8px 0", borderBottom:"1px solid #f3f4f6", fontSize:13 }}>
                  <span style={{ fontWeight:500 }}>{item.name}</span>
                  <div style={{ display:"flex", gap:12, alignItems:"center" }}>
                    <span style={{ color:"#6b7280" }}>Stock: {item.totalStock}</span>
                    <span style={{ color:"#F47920", fontWeight:600 }}>Order: {item.suggestedOrder} units</span>
                    <input type="number" min="0" style={{ width:60, padding:"2px 6px", borderRadius:5, border:"1px solid #d1d5db", fontSize:12, fontFamily:"inherit" }}
                      value={item.unitPrice || 0}
                      placeholder="₹"
                      onChange={(e) => setRepItems((prev) => prev.map((p,idx) => idx===i?{...p,unitPrice:parseFloat(e.target.value)||0}:p))}/>
                  </div>
                </div>
              ))}
            </div>

            <label style={S.label}>Supplier Name *</label>
            <input style={S.input} placeholder="Supplier name" value={repSupplier.name} onChange={(e) => setRepSupplier((f) => ({ ...f, name:e.target.value }))}/>
            <label style={S.label}>Supplier Email</label>
            <input style={S.input} type="email" placeholder="supplier@email.com" value={repSupplier.email} onChange={(e) => setRepSupplier((f) => ({ ...f, email:e.target.value }))}/>

            <button style={{ ...S.btn("#dc2626"), width:"100%", padding:11, fontSize:14 }} onClick={createReplenishPO}>
              🔁 Create Purchase Order
            </button>
          </div>
        </div>
      )}

      {/* ══ WAREHOUSE STOCK ══ */}
      {tab === "stock" && (
        <div>
          {/* controls */}
          <div style={{ display:"flex", gap:8, marginBottom:14, flexWrap:"wrap", alignItems:"center" }}>
            <select style={{ padding:"7px 10px", borderRadius:7, border:"1px solid #d1d5db", fontSize:12, fontFamily:"inherit" }}
              value={stockWh} onChange={(e) => setStockWh(e.target.value)}>
              <option value="">All Warehouses</option>
              {warehouses.map((w) => <option key={w._id} value={w._id}>{w.name} ({w.code})</option>)}
            </select>
            <input style={{ flex:1, minWidth:160, padding:"7px 10px", borderRadius:7, border:"1px solid #d1d5db", fontSize:12, fontFamily:"inherit" }}
              placeholder="Search SKU or product…" value={stockSearch} onChange={(e) => setStockSearch(e.target.value)}/>
            <button style={{ ...S.btnOut, fontSize:12 }} onClick={async () => {
              setReportLoading(true);
              try { const d = await api("/wms/stock/report"); setReportData(d); } catch(e) { toast(e.message,"error"); }
              setReportLoading(false);
            }}>📊 {reportLoading?"Loading…":"Generate Report"}</button>
            {reportData && (
              <button style={{ ...S.btn("#15803d"), fontSize:12 }} onClick={() => {
                const wb   = XLSX.utils.book_new();
                const whs  = reportData.warehouses;
                const headers = ["SKU","Product Name","Category","HSN","Unit","MRP (₹)","Cost (₹)","Status",...whs.map((w)=>w.name+" ("+w.code+")"),"Total Units"];
                const rows = reportData.report.map((r) => [r.sku,r.productName,r.category||"",r.hsn||"",r.unit||"pcs",r.mrp||0,r.costPrice||0,r.status,...whs.map((w)=>r[w.code]||0),r.total]);
                const ws = XLSX.utils.aoa_to_sheet([headers,...rows]);
                ws["!cols"] = [{wch:16},{wch:36},{wch:16},{wch:10},{wch:8},{wch:10},{wch:10},{wch:14},...whs.map(()=>({wch:20})),{wch:12}];
                XLSX.utils.book_append_sheet(wb,ws,"Inventory Report");
                // Sheet 2: per warehouse
                const s2headers = ["Warehouse","Code","Total SKUs","Total Units","Low Stock SKUs","Out of Stock SKUs"];
                const s2rows = whs.map((w) => {
                  const whRows = reportData.report.filter((r)=>(r[w.code]||0)>0);
                  return [w.name,w.code,whRows.length,reportData.report.reduce((a,r)=>a+(r[w.code]||0),0),reportData.report.filter((r)=>(r[w.code]||0)>0&&(r[w.code]||0)<=10).length,reportData.report.filter((r)=>(r[w.code]||0)===0).length];
                });
                const ws2 = XLSX.utils.aoa_to_sheet([s2headers,...s2rows]);
                XLSX.utils.book_append_sheet(wb,ws2,"Warehouse Summary");
                // Sheet 3: alerts
                const alertRows = reportData.report.filter((r)=>r.status!=="In Stock");
                if (alertRows.length) {
                  const ws3 = XLSX.utils.aoa_to_sheet([["SKU","Product","Status",...whs.map((w)=>w.name),"Total"],...alertRows.map((r)=>[r.sku,r.productName,r.status,...whs.map((w)=>r[w.code]||0),r.total])]);
                  XLSX.utils.book_append_sheet(wb,ws3,"Alerts");
                }
                XLSX.writeFile(wb,"3June_Warehouse_Stock_"+new Date().toISOString().split("T")[0]+".xlsx");
                toast("Excel downloaded ✓","success");
              }}>⬇ Download Excel</button>
            )}
            <button style={{ ...S.btnOut, fontSize:12 }} onClick={downloadSampleExcel}>📥 Sample Excel</button>
            {/* Excel mode selector */}
            <div style={{ display:"flex", background:"#f3f4f6", borderRadius:7, padding:2, gap:2 }}>
              {[["replace","🔄 Set Qty"],["add","➕ Add"],["subtract","➖ Subtract"]].map(([m,l])=>(
                <button key={m} onClick={()=>setExcelMode(m)}
                  style={{ padding:"5px 10px", borderRadius:5, border:"none", fontSize:11, fontWeight:excelMode===m?700:400, background:excelMode===m?"#fff":"transparent", color:excelMode===m?(m==="replace"?"#6366f1":m==="add"?"#15803d":"#dc2626"):"#6b7280", cursor:"pointer", fontFamily:"inherit", boxShadow:excelMode===m?"0 1px 3px rgba(0,0,0,0.1)":"none" }}>
                  {l}
                </button>
              ))}
            </div>
            <button style={{ ...S.btn("#6366f1"), fontSize:12 }} onClick={() => stockFileRef.current?.click()} disabled={excelUploading}>
              {excelUploading ? "⏳ Reading…" : "⬆ Upload Excel"}
            </button>
            <input ref={stockFileRef} type="file" accept=".xlsx,.xls,.csv" style={{ display:"none" }}
              onChange={(e) => { if(e.target.files[0]) handleStockExcel(e.target.files[0], stockWh||warehouses[0]?._id||""); e.target.value=""; }}/>
            <button style={{ ...S.btn(stockScanMode?"#dc2626":"#0ea5e9"), fontSize:12 }} onClick={() => { setStockScanMode(!stockScanMode); setStockScanResult(null); setStockScanInput(""); setTimeout(()=>stockScanRef.current?.focus(),100); }}>
              {stockScanMode ? "✕ Close Scanner" : "🔍 Barcode Scanner"}
            </button>
            <button style={S.btn()} onClick={() => { setEditStock(null); setStockForm({ warehouseId:warehouses[0]?._id||"", sku:"", productName:"", category:"", hsn:"", unit:"pcs", qty:0, minQty:0, costPrice:0, mrp:0, location:"", notes:"" }); setStockModal(true); }}>+ Add Stock</button>
          </div>

          {/* ── BARCODE SCANNER PANEL ── */}
          {stockScanMode && (
            <div style={{ ...S.card, marginBottom:14, border:"2px solid #0ea5e9" }}>
              <div style={{ padding:"10px 16px", background:"#f0f9ff", borderBottom:"1px solid #bae6fd", display:"flex", alignItems:"center", gap:10 }}>
                <span style={{ fontSize:16 }}>🔍</span>
                <span style={{ fontWeight:700, fontSize:13, color:"#0369a1" }}>Barcode Scanner — Update Stock Qty</span>
                <span style={{ fontSize:11, color:"#6b7280" }}>Connect USB/Bluetooth scanner or type SKU</span>
              </div>
              <div style={{ padding:16 }}>
                <div style={{ display:"flex", gap:10, marginBottom:14 }}>
                  <input ref={stockScanRef} style={{ flex:1, padding:"10px 14px", borderRadius:8, border:"2px solid #0ea5e9", fontSize:14, fontFamily:"monospace", outline:"none" }}
                    placeholder="Scan barcode or type SKU and press Enter…"
                    value={stockScanInput}
                    onChange={(e) => setStockScanInput(e.target.value)}
                    onKeyDown={(e) => { if(e.key==="Enter") handleStockScan(stockScanInput); }}
                    autoFocus/>
                  <button style={S.btn("#0ea5e9")} onClick={() => handleStockScan(stockScanInput)}>Search</button>
                </div>

                {stockScanResult && !stockScanResult._notFound && (
                  <div style={{ background:"#f0fdf4", borderRadius:8, border:"1px solid #bbf7d0", padding:16 }}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:12 }}>
                      <div>
                        <div style={{ fontWeight:700, fontSize:15 }}>{stockScanResult.productName}</div>
                        <div style={{ fontSize:12, color:"#6b7280", marginTop:2 }}>
                          SKU: <strong style={{ fontFamily:"monospace", color:"#F47920" }}>{stockScanResult.sku}</strong> ·
                          Warehouse: {stockScanResult.warehouseName} ·
                          Location: {stockScanResult.location||"—"}
                        </div>
                      </div>
                      <div style={{ textAlign:"right" }}>
                        <div style={{ fontSize:28, fontWeight:700, color:"#15803d" }}>{stockScanResult.qty}</div>
                        <div style={{ fontSize:10, color:"#6b7280" }}>current qty</div>
                      </div>
                    </div>
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10, marginBottom:12 }}>
                      {[["set","📌 Set to"],["add","➕ Add"],["subtract","➖ Subtract"]].map(([v,l])=>(
                        <button key={v} onClick={() => setStockScanAdj(v)} style={{ padding:"8px", borderRadius:7, border:"2px solid "+(stockScanAdj===v?"#0ea5e9":"#e5e7eb"), background:stockScanAdj===v?"#f0f9ff":"#fff", cursor:"pointer", fontFamily:"inherit", fontSize:12, fontWeight:stockScanAdj===v?700:400, color:stockScanAdj===v?"#0ea5e9":"#374151" }}>{l}</button>
                      ))}
                    </div>
                    <div style={{ display:"flex", gap:10, alignItems:"center" }}>
                      <input type="number" min="0" style={{ flex:1, padding:"9px 12px", borderRadius:7, border:"1px solid #d1d5db", fontSize:16, fontFamily:"inherit", outline:"none", fontWeight:700, textAlign:"center" }}
                        value={stockScanQty} onChange={(e) => setStockScanQty(parseInt(e.target.value)||0)}/>
                      <div style={{ fontSize:13, color:"#6b7280" }}>
                        → New qty: <strong style={{ color:"#15803d", fontSize:16 }}>
                          {stockScanAdj==="set"?stockScanQty:stockScanAdj==="add"?stockScanResult.qty+stockScanQty:Math.max(0,stockScanResult.qty-stockScanQty)}
                        </strong>
                      </div>
                      <button style={S.btn("#15803d")} onClick={applyStockScanUpdate}>✓ Update</button>
                      <button style={S.btnOut} onClick={() => { setStockScanResult(null); setStockScanInput(""); stockScanRef.current?.focus(); }}>Clear</button>
                    </div>
                  </div>
                )}

                {stockScanResult?._notFound && (
                  <div style={{ background:"#fef2f2", borderRadius:8, border:"1px solid #fecaca", padding:16, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                    <div>
                      <div style={{ fontWeight:600, color:"#dc2626" }}>❌ SKU not found: {stockScanResult.sku}</div>
                      <div style={{ fontSize:12, color:"#6b7280", marginTop:4 }}>This SKU doesn't exist in your warehouse stock yet.</div>
                    </div>
                    <button style={S.btn()} onClick={() => { setStockForm({ ...stockForm, sku:stockScanResult.sku, warehouseId:warehouses[0]?._id||"" }); setStockModal(true); setStockScanResult(null); }}>+ Add This SKU</button>
                  </div>
                )}

                {!stockScanResult && (
                  <div style={{ textAlign:"center", padding:"16px 0", color:"#9ca3af", fontSize:12 }}>
                    <div style={{ fontSize:32, marginBottom:8 }}>📦</div>
                    Scan a barcode or type a SKU to look up and update stock
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── EXCEL PREVIEW ── */}
          {excelPreview && (
            <div style={{ ...S.card, marginBottom:14, border:"2px solid #6366f1" }}>
              <div style={{ padding:"10px 16px", background:"#f5f3ff", borderBottom:"1px solid #ddd6fe", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                <div>
                  <span style={{ fontWeight:700, fontSize:13, color:"#6366f1" }}>
                    📊 Excel Preview — {excelPreview.rows.length} rows
                    <span style={{ marginLeft:8, background:excelMode==="replace"?"#ede9fe":excelMode==="add"?"#f0fdf4":"#fef2f2", color:excelMode==="replace"?"#6366f1":excelMode==="add"?"#15803d":"#dc2626", padding:"1px 8px", borderRadius:10, fontSize:10, fontWeight:700 }}>
                      {excelMode==="replace"?"🔄 Set Qty":excelMode==="add"?"➕ Add to existing":"➖ Subtract from existing"}
                    </span>
                  </span>
                  {excelPreview.errors.length>0 && <span style={{ fontSize:11, color:"#dc2626", marginLeft:10 }}>⚠ {excelPreview.errors.length} issues found</span>}
                </div>
                <div style={{ display:"flex", gap:8 }}>
                  <button style={S.btnOut} onClick={() => setExcelPreview(null)}>✕ Cancel</button>
                  <button style={S.btn("#6366f1")} onClick={importStockFromExcel}>
                    {excelMode==="replace"?"⬇ Set Qty for":excelMode==="add"?"➕ Add to":"➖ Subtract from"} {excelPreview.rows.filter((r)=>r._valid).length} Items
                  </button>
                </div>
              </div>
              {excelPreview.errors.length>0 && (
                <div style={{ padding:"8px 16px", background:"#fef2f2", borderBottom:"1px solid #fecaca" }}>
                  {excelPreview.errors.slice(0,3).map((e,i)=><div key={i} style={{ fontSize:11, color:"#dc2626" }}>⚠ {e}</div>)}
                  {excelPreview.errors.length>3 && <div style={{ fontSize:11, color:"#dc2626" }}>...and {excelPreview.errors.length-3} more</div>}
                </div>
              )}
              <div style={{ overflowX:"auto", maxHeight:280, overflowY:"auto" }}>
                <table style={{ width:"100%", borderCollapse:"collapse" }}>
                  <thead style={{ position:"sticky", top:0 }}><tr>
                    {["#","SKU","Product Name","Category","Qty","Min Qty","MRP","Cost","Location","Status"].map((h)=><th key={h} style={S.th}>{h}</th>)}
                  </tr></thead>
                  <tbody>
                    {excelPreview.rows.map((r,i)=>(
                      <tr key={i} style={{ background:r._valid?"":"#fff5f5" }}>
                        <td style={{ ...S.td, fontSize:10, color:"#9ca3af" }}>{i+1}</td>
                        <td style={{ ...S.td, fontFamily:"monospace", fontSize:11, color:"#F47920" }}>{r.sku||<span style={{color:"#dc2626"}}>MISSING</span>}</td>
                        <td style={{ ...S.td, fontSize:12 }}>{r.productName||<span style={{color:"#dc2626"}}>MISSING</span>}</td>
                        <td style={{ ...S.td, fontSize:11, color:"#6b7280" }}>{r.category||"—"}</td>
                        <td style={{ ...S.td, fontWeight:700 }}>{r.qty}</td>
                        <td style={{ ...S.td, fontSize:11 }}>{r.minQty||0}</td>
                        <td style={{ ...S.td, fontSize:11 }}>{r.mrp?INR(r.mrp):"—"}</td>
                        <td style={{ ...S.td, fontSize:11 }}>{r.costPrice?INR(r.costPrice):"—"}</td>
                        <td style={{ ...S.td, fontSize:11, fontFamily:"monospace" }}>{r.location||"—"}</td>
                        <td style={S.td}>{r._valid?<span style={{color:"#15803d",fontSize:10,fontWeight:700}}>✓ Valid</span>:<span style={{color:"#dc2626",fontSize:10,fontWeight:700}}>✗ Error</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ padding:"10px 16px", background:"#f5f3ff", borderTop:"1px solid #ddd6fe", fontSize:12, color:"#6b7280" }}>
                ✅ Valid: {excelPreview.rows.filter((r)=>r._valid).length} rows will be imported ·
                ❌ Invalid: {excelPreview.rows.filter((r)=>!r._valid).length} rows will be skipped ·
                Warehouse: {warehouses.find((w)=>w._id===excelPreview.warehouseId)?.name||"—"}
              </div>
            </div>
          )}

          {/* report summary pills */}
          {reportData && (
            <div style={{ display:"flex", gap:10, flexWrap:"wrap", marginBottom:14 }}>
              {reportData.warehouses.map((wh,i) => {
                const COLORS=["#F47920","#6366f1","#14b8a6","#ec4899","#f59e0b"];
                const color=COLORS[i%COLORS.length];
                const units=reportData.report.reduce((a,r)=>a+(r[wh.code]||0),0);
                const skus=reportData.report.filter((r)=>(r[wh.code]||0)>0).length;
                return (
                  <div key={wh._id} style={{ background:"#fff", border:"1px solid "+color+"30", borderLeft:"4px solid "+color, borderRadius:8, padding:"10px 14px", minWidth:140 }}>
                    <div style={{ fontWeight:700, color, fontSize:12, marginBottom:2 }}>{wh.isPrimary?"⭐ ":""}{wh.name}</div>
                    <div style={{ fontSize:10, color:"#6b7280", marginBottom:4 }}>{wh.code}</div>
                    <div style={{ display:"flex", gap:12 }}>
                      <div><div style={{ fontSize:16, fontWeight:700, color }}>{units}</div><div style={{ fontSize:9, color:"#9ca3af" }}>units</div></div>
                      <div><div style={{ fontSize:16, fontWeight:700 }}>{skus}</div><div style={{ fontSize:9, color:"#9ca3af" }}>SKUs</div></div>
                    </div>
                  </div>
                );
              })}
              <div style={{ background:"#1A3A5C", borderRadius:8, padding:"10px 14px", minWidth:140, color:"#fff" }}>
                <div style={{ fontSize:12, fontWeight:700, marginBottom:4 }}>📦 Total Stock</div>
                <div style={{ fontSize:20, fontWeight:700 }}>{reportData.totalUnits}</div>
                <div style={{ fontSize:10, opacity:.7 }}>{reportData.totalSkus} SKUs</div>
              </div>
            </div>
          )}

          {/* stock table */}
          <div style={S.card}>
            <div style={S.cardHdr}>
              <span>Stock Entries ({(() => { let s=stock; if(stockWh) s=s.filter((x)=>x.warehouseId===stockWh); if(stockSearch){const q=stockSearch.toLowerCase();s=s.filter((x)=>x.sku.toLowerCase().includes(q)||x.productName.toLowerCase().includes(q));} return s.length; })()})</span>
              <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                <span style={{ fontSize:11, color:"#9ca3af" }}>Sort:</span>
                {[["productName","Name"],["sku","SKU"],["qty","Qty"],["warehouseCode","WH"],["status","Status"]].map(([f,l])=>(
                  <button key={f} onClick={()=>setStockSort((prev)=>({ field:f, dir:prev.field===f&&prev.dir==="asc"?"desc":"asc" }))}
                    style={{ padding:"3px 8px", borderRadius:5, border:"1px solid "+(stockSort.field===f?"#6366f1":"#e5e7eb"), background:stockSort.field===f?"#f5f3ff":"#fff", color:stockSort.field===f?"#6366f1":"#6b7280", fontSize:10, fontWeight:stockSort.field===f?700:400, cursor:"pointer", fontFamily:"inherit" }}>
                    {l}{stockSort.field===f?(stockSort.dir==="asc"?" ↑":" ↓"):""}
                  </button>
                ))}
              </div>
            </div>
            {loading ? <div style={{ textAlign:"center", padding:40, color:"#9ca3af" }}>Loading…</div> : (() => {
              let rows = [...stock];
              if (stockWh) rows = rows.filter((x)=>x.warehouseId===stockWh);
              if (stockSearch) { const q=stockSearch.toLowerCase(); rows=rows.filter((x)=>x.sku.toLowerCase().includes(q)||x.productName.toLowerCase().includes(q)); }
              // Apply sort
              rows.sort((a,b) => {
                let av = a[stockSort.field]||"", bv = b[stockSort.field]||"";
                if (stockSort.field === "qty") { av = a.qty||0; bv = b.qty||0; return stockSort.dir==="asc"?av-bv:bv-av; }
                if (stockSort.field === "status") { const o={"❌ Out":0,"⚠️ Low":1,"✅ OK":2}; av=a.qty===0?"❌ Out":a.qty<=a.minQty?"⚠️ Low":"✅ OK"; bv=b.qty===0?"❌ Out":b.qty<=b.minQty?"⚠️ Low":"✅ OK"; return stockSort.dir==="asc"?(o[av]||0)-(o[bv]||0):(o[bv]||0)-(o[av]||0); }
                av = String(av).toLowerCase(); bv = String(bv).toLowerCase();
                return stockSort.dir==="asc"?av.localeCompare(bv):bv.localeCompare(av);
              });
              if (!rows.length) return (
                <div style={{ textAlign:"center", padding:50 }}>
                  <div style={{ fontSize:36, marginBottom:10 }}>📦</div>
                  <div style={{ fontWeight:600, marginBottom:8 }}>No stock entries yet</div>
                  <button style={S.btn()} onClick={()=>{ setEditStock(null); setStockForm({ warehouseId:warehouses[0]?._id||"", sku:"", productName:"", category:"", hsn:"", unit:"pcs", qty:0, minQty:0, costPrice:0, mrp:0, location:"", notes:"" }); setStockModal(true); }}>+ Add First Item</button>
                </div>
              );
              return (
                <div style={{ overflowX:"auto" }}>
                  <table style={{ width:"100%", borderCollapse:"collapse" }}>
                    <thead><tr>
                      {["Warehouse","SKU","Product","Category","Location","Qty ↕","Min Qty","MRP","Cost","Status",""].map((h)=><th key={h} style={S.th}>{h}</th>)}
                    </tr></thead>
                    <tbody>
                      {rows.map((s) => {
                        const low  = s.qty === 0;
                        const warn = s.qty > 0 && s.qty <= s.minQty;
                        return (
                          <tr key={s._id} onMouseEnter={(e)=>e.currentTarget.style.background="#fafafa"} onMouseLeave={(e)=>e.currentTarget.style.background=""}>
                            <td style={S.td}><span style={{ background:"#F4792018", color:"#F47920", padding:"2px 8px", borderRadius:10, fontSize:11, fontWeight:600 }}>{s.warehouseCode}</span></td>
                            <td style={{ ...S.td, fontFamily:"monospace", fontSize:11, color:"#F47920", fontWeight:600 }}>{s.sku}</td>
                            <td style={{ ...S.td, fontWeight:500, maxWidth:180, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{s.productName}</td>
                            <td style={{ ...S.td, fontSize:11, color:"#6b7280" }}>{s.category||"—"}</td>
                            <td style={{ ...S.td, fontSize:11, color:"#6b7280", fontFamily:"monospace" }}>{s.location||"—"}</td>
                            <td style={{ ...S.td, fontWeight:700, fontSize:15, color:low?"#dc2626":warn?"#b45309":"#15803d" }}>{s.qty}</td>
                            <td style={{ ...S.td, fontSize:11, color:"#9ca3af" }}>{s.minQty||0}</td>
                            <td style={{ ...S.td, fontWeight:600 }}>{s.mrp?INR(s.mrp):"—"}</td>
                            <td style={{ ...S.td, fontSize:11, color:"#6b7280" }}>{s.costPrice?INR(s.costPrice):"—"}</td>
                            <td style={S.td}>
                              <span style={{ background:low?"#fef2f2":warn?"#fffbeb":"#f0fdf4", color:low?"#dc2626":warn?"#b45309":"#15803d", padding:"2px 8px", borderRadius:10, fontSize:10, fontWeight:700 }}>
                                {low?"❌ Out":warn?"⚠️ Low":"✅ OK"}
                              </span>
                            </td>
                            <td style={S.td}>
                              <div style={{ display:"flex", gap:5 }}>
                                <button style={{ ...S.btn("#6366f1"), fontSize:11, padding:"3px 8px" }} onClick={()=>{ setAdjItem(s); setAdjQty(0); setAdjReason(""); setAdjModal(true); }}>±Adj</button>
                                <button style={{ ...S.btnOut, fontSize:11, padding:"3px 8px" }} onClick={()=>{ setEditStock(s); setStockForm({ warehouseId:s.warehouseId, sku:s.sku, productName:s.productName, category:s.category, hsn:s.hsn, unit:s.unit, qty:s.qty, minQty:s.minQty, costPrice:s.costPrice, mrp:s.mrp, location:s.location, notes:s.notes }); setStockModal(true); }}>Edit</button>
                                <button style={{ ...S.btnOut, fontSize:11, padding:"3px 8px", color:"#dc2626", borderColor:"#fecaca" }} onClick={async()=>{ if(!window.confirm("Delete?")) return; await api("/wms/stock/"+s._id,{method:"DELETE"}); toast("Deleted","success"); loadAll(); }}>Del</button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr style={{ background:"#f9fafb" }}>
                        <td colSpan={5} style={{ ...S.td, fontWeight:700 }}>Total ({rows.length} items)</td>
                        <td style={{ ...S.td, fontWeight:700, fontSize:15, color:"#1A3A5C" }}>{rows.reduce((a,s)=>a+s.qty,0)}</td>
                        <td colSpan={5} style={S.td}/>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* ── ADD/EDIT STOCK MODAL ── */}
      {stockModal && (
        <div style={S.overlay} onClick={(e)=>e.target===e.currentTarget&&setStockModal(false)}>
          <div style={{ ...S.modal, width:560 }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
              <span style={{ fontWeight:700, fontSize:16 }}>{editStock?"Edit Stock Entry":"Add Stock to Warehouse"}</span>
              <button onClick={()=>setStockModal(false)} style={{ background:"none", border:"none", fontSize:20, cursor:"pointer", color:"#9ca3af" }}>✕</button>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
              <div style={{ gridColumn:"1/-1" }}>
                <label style={S.label}>Warehouse *</label>
                <select style={{ ...S.input, marginBottom:10 }} value={stockForm.warehouseId} onChange={(e)=>setStockForm((f)=>({...f,warehouseId:e.target.value}))}>
                  <option value="">— Select Warehouse —</option>
                  {warehouses.map((w)=><option key={w._id} value={w._id}>{w.name} ({w.code})</option>)}
                </select>
              </div>
              <div>
                <label style={S.label}>SKU *</label>
                <input style={{ ...S.input, marginBottom:10 }} placeholder="e.g. LP-BLK-001" value={stockForm.sku} onChange={(e)=>setStockForm((f)=>({...f,sku:e.target.value}))}/>
              </div>
              <div>
                <label style={S.label}>Product Name *</label>
                <input style={{ ...S.input, marginBottom:10 }} placeholder="Product name" value={stockForm.productName} onChange={(e)=>setStockForm((f)=>({...f,productName:e.target.value}))}/>
              </div>
              <div>
                <label style={S.label}>Category</label>
                <input style={{ ...S.input, marginBottom:10 }} placeholder="e.g. Belts" value={stockForm.category} onChange={(e)=>setStockForm((f)=>({...f,category:e.target.value}))}/>
              </div>
              <div>
                <label style={S.label}>HSN Code</label>
                <input style={{ ...S.input, marginBottom:10 }} placeholder="42033000" value={stockForm.hsn} onChange={(e)=>setStockForm((f)=>({...f,hsn:e.target.value}))}/>
              </div>
              <div>
                <label style={S.label}>Quantity *</label>
                <input type="number" min="0" style={{ ...S.input, marginBottom:10 }} value={stockForm.qty} onChange={(e)=>setStockForm((f)=>({...f,qty:parseInt(e.target.value)||0}))}/>
              </div>
              <div>
                <label style={S.label}>Min Qty (reorder alert)</label>
                <input type="number" min="0" style={{ ...S.input, marginBottom:10 }} value={stockForm.minQty} onChange={(e)=>setStockForm((f)=>({...f,minQty:parseInt(e.target.value)||0}))}/>
              </div>
              <div>
                <label style={S.label}>MRP (₹)</label>
                <input type="number" min="0" style={{ ...S.input, marginBottom:10 }} value={stockForm.mrp} onChange={(e)=>setStockForm((f)=>({...f,mrp:parseFloat(e.target.value)||0}))}/>
              </div>
              <div>
                <label style={S.label}>Cost Price (₹)</label>
                <input type="number" min="0" style={{ ...S.input, marginBottom:10 }} value={stockForm.costPrice} onChange={(e)=>setStockForm((f)=>({...f,costPrice:parseFloat(e.target.value)||0}))}/>
              </div>
              <div>
                <label style={S.label}>Unit</label>
                <select style={{ ...S.input, marginBottom:10 }} value={stockForm.unit} onChange={(e)=>setStockForm((f)=>({...f,unit:e.target.value}))}>
                  {["pcs","kg","g","m","dozen","box","pair"].map((u)=><option key={u}>{u}</option>)}
                </select>
              </div>
              <div>
                <label style={S.label}>Rack / Location</label>
                <input style={{ ...S.input, marginBottom:10 }} placeholder="e.g. A-3-2" value={stockForm.location} onChange={(e)=>setStockForm((f)=>({...f,location:e.target.value}))}/>
              </div>
              <div style={{ gridColumn:"1/-1" }}>
                <label style={S.label}>Notes</label>
                <input style={{ ...S.input, marginBottom:10 }} placeholder="Any notes" value={stockForm.notes} onChange={(e)=>setStockForm((f)=>({...f,notes:e.target.value}))}/>
              </div>
            </div>
            <button style={{ ...S.btn(), width:"100%", padding:11, fontSize:14 }} onClick={async()=>{
              if(!stockForm.warehouseId){toast("Select a warehouse","error");return;}
              if(!stockForm.sku){toast("Enter SKU","error");return;}
              if(!stockForm.productName){toast("Enter product name","error");return;}
              try {
                if(editStock) {
                  await api("/wms/stock/"+editStock._id,{method:"PUT",body:JSON.stringify(stockForm)});
                  toast("Stock updated ✓","success");
                } else {
                  await api("/wms/stock",{method:"POST",body:JSON.stringify(stockForm)});
                  toast("Stock added ✓","success");
                }
                setStockModal(false); setEditStock(null); loadAll();
              } catch(e){toast(e.message,"error");}
            }}>{editStock?"Save Changes":"Add to Warehouse"}</button>
          </div>
        </div>
      )}

      {/* ── ADJUST QTY MODAL ── */}
      {adjModal && adjItem && (
        <div style={S.overlay} onClick={(e)=>e.target===e.currentTarget&&setAdjModal(false)}>
          <div style={{ ...S.modal, width:380 }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
              <span style={{ fontWeight:700, fontSize:16 }}>Adjust Stock Quantity</span>
              <button onClick={()=>setAdjModal(false)} style={{ background:"none", border:"none", fontSize:20, cursor:"pointer", color:"#9ca3af" }}>✕</button>
            </div>
            <div style={{ background:"#f9fafb", borderRadius:8, padding:"10px 14px", marginBottom:16 }}>
              <div style={{ fontWeight:600 }}>{adjItem.productName}</div>
              <div style={{ fontSize:12, color:"#6b7280" }}>SKU: {adjItem.sku} · {adjItem.warehouseName} · Current: <strong>{adjItem.qty}</strong></div>
            </div>
            <label style={S.label}>Adjustment (+ to add, - to remove)</label>
            <input type="number" style={{ ...S.input, marginBottom:10, fontSize:18, fontWeight:700, textAlign:"center" }} value={adjQty} onChange={(e)=>setAdjQty(parseInt(e.target.value)||0)}/>
            <div style={{ textAlign:"center", fontSize:13, color:"#6b7280", marginBottom:10 }}>
              New qty: <strong style={{ color: Math.max(0,adjItem.qty+adjQty)===0?"#dc2626":"#15803d", fontSize:16 }}>{Math.max(0,adjItem.qty+adjQty)}</strong>
            </div>
            <label style={S.label}>Reason</label>
            <input style={{ ...S.input, marginBottom:16 }} placeholder="e.g. Stock count, damage, received PO" value={adjReason} onChange={(e)=>setAdjReason(e.target.value)}/>
            <div style={{ display:"flex", gap:10 }}>
              <button style={{ ...S.btnOut, flex:1 }} onClick={()=>setAdjModal(false)}>Cancel</button>
              <button style={{ ...S.btn(), flex:2 }} onClick={async()=>{
                try {
                  await api("/wms/stock/"+adjItem._id+"/adjust",{method:"PUT",body:JSON.stringify({adjustment:adjQty,reason:adjReason})});
                  toast("Stock adjusted ✓","success"); setAdjModal(false); loadAll();
                } catch(e){toast(e.message,"error");}
              }}>Apply Adjustment</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
